import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  WritableSignal,
  AfterViewInit,
  signal,
  HostListener,
  Inject,
  TemplateRef,
  ViewContainerRef,
  OnDestroy,
  Injector,
} from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SelectComponent } from '../../../shared/select/select.component';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
  MatPrefix,
  MatError,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import {
  JsEmail,
  TemplateVariable,
  PublishedSnapshot,
  LanguageValidation,
  ValidationError,
} from '../../../_interfaces/email.interface';
import {
  TranslationJobDoc,
  LangUiState,
  EnqueueTranslateRequest,
  EnqueueTranslateResponse,
} from '../../../_interfaces/translation-job.interface';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ActivatedRoute, Router } from '@angular/router';
import { CacheService } from '../../../_services/cache.service';
import { EmailHashUtil } from '../../../_utils/hash.util';
import { sortLanguageCodes, getNewId } from '../../../_utils/utils';
import {
  EmailDiffDialogComponent,
  EmailDiffDialogData,
} from '../email-diff-dialog/email-diff-dialog.component';
import { EmailFormComponent } from '../email-form/email-form.component';
import {
  EmailCloneDialogComponent,
  CloneDialogResult,
} from '../email-clone-dialog/email-clone-dialog.component';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import {
  VariableDialogComponent,
  VariableDialogModel,
} from '../../../shared/variable-dialog/variable-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EmailTemplatePreviewComponent } from '../email-template-preview/email-template-preview.component';
import { TestEmailDialogComponent } from '../../../shared/test-email-dialog/test-email-dialog.component';
import { CodemirrorModule, CodemirrorComponent } from '@ctrl/ngx-codemirror';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import _ from 'lodash';
import { FirebaseOptimisticService } from '../../../_services/firebase-optimistic.service';
import { FirebaseService } from '../../../_services/firebase.service';
import { StorageService } from '../../../_services/storage.service';
import { NoSpacesDirective } from '../../../_directives/no-space.directive';
import { languages, isRtlLanguage, getLanguageName } from '../../../_datas/languages.data';
import { AlertService } from '../../../_services/alert.service';
import { EmailService } from '../../../_services/email.service';
import { FirestoreStoreService } from '../../../_services/firestore-store.service';
import { AuthService } from '../../../_services/auth.service';
import { html as beautifyHtml } from 'js-beautify';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { computed, effect } from '@angular/core';

// Import CodeMirror modes for syntax highlighting
import 'codemirror/mode/xml/xml';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/css/css';
import 'codemirror/mode/htmlmixed/htmlmixed';

// Import CodeMirror addons for enhanced features
import 'codemirror/addon/edit/closetag';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/xml-fold';
import 'codemirror/addon/hint/show-hint';
import 'codemirror/addon/hint/html-hint';

/** HTML validation error type */
interface HtmlValidationError {
  message: string;
  line?: number;
  column?: number;
  type: 'error' | 'warning';
}

/** A queued/translating doc older than this is treated as 'stuck' and
 * surfaced to the UI as 'failed' so the user gets a ↻ retry escape hatch
 * instead of an infinite spinner. Worker normally finishes well within
 * ~3 minutes (16 langs × ~25s p99 + retries); 10 minutes leaves comfortable
 * headroom while bounding the worst case. */
const STUCK_AFTER_MS = 10 * 60 * 1000;

/** Minimum gap between a user keystroke in a lang field and our auto-copy
 * of worker output into that same field. Within this window we suppress
 * the copy so the user doesn't lose mid-keystroke edits. */
const USER_EDIT_SUPPRESS_MS = 5_000;

@Component({
  selector: 'app-email-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatIcon,
    MatInput,
    MatPrefix,
    MatMenuModule,
    MatTooltip,
    SelectComponent,
    CodemirrorModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    OverlayModule,
  ],
  templateUrl: './email-editor.component.html',
  styleUrl: './email-editor.component.scss',
})
export class EmailEditorComponent implements AfterViewInit, OnDestroy {
  @ViewChild('emForm') emForm!: NgForm;
  @ViewChild('etestForm') etestForm!: NgForm;
  @ViewChild('codemirror') codemirrorComponent!: CodemirrorComponent;
  @ViewChild('fullscreenTemplate') fullscreenTemplate!: TemplateRef<any>;
  @ViewChild('langButtonsContainer') langButtonsContainer!: ElementRef;

  // Overlay for fullscreen preview
  private fullscreenOverlayRef: OverlayRef | null = null;
  emailInitial!: JsEmail;
  emailForm: FormGroup;
  testForm: FormGroup;
  languageCodes: WritableSignal<string[]> = this.cc.languageCodes;
  canScrollLeft = signal(false);
  canScrollRight = signal(false);
  unSubscribe = new Subject<void>();
  currLangCode = signal<string>('');
  private variableDialogRef: MatDialogRef<VariableDialogComponent> | null =
    null;
  translateInProgress: boolean = false;
  sendingEmail: boolean = false;
  lastTranslatedHtml: string = '';
  lastTranslatedSubject: string = '';
  lastTranslatedAt = signal<Date | null>(null);
  languages: { code: string; name: string }[] = languages;
  initialVariables: TemplateVariable[] = [];

  // HTML validation state
  htmlValidationErrors = signal<HtmlValidationError[]>([]);
  isHtmlValid = signal<boolean>(true);

  // Subject validation state
  subjectValidationErrors = signal<string[]>([]);

  // NEW: Content hash and publishing state
  currentContentHash = signal<string>('');
  isPublishing = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  languageValidationStates = signal<{ [lang: string]: LanguageValidation }>({});

  // Variable display mode (false = keys, true = values)
  showVariableValues = signal<boolean>(false);

  // Track if form has unsaved changes
  hasUnsavedChanges = signal<boolean>(false);

  // Flag to prevent effect from resetting form after initial load
  private isInitialized = false;

  // Preview controls
  previewViewMode = signal<'desktop' | 'mobile'>('desktop');
  previewDarkMode = signal<boolean>(false);
  previewFullscreen = signal<boolean>(false);
  previewCustomWidth = signal<number>(800);
  previewCustomHeight = signal<number>(600);

  // AI Assistant state
  aiAssistantOpen = signal<boolean>(false);
  aiAssistantLoading = signal<boolean>(false);
  aiFormattingLoading = signal<boolean>(false); // Separate loading state for format button
  aiInstruction = signal<string>('');
  aiPreviousHtml = signal<string>(''); // For undo functionality
  aiCanUndo = signal<boolean>(false);

  // AI Image Upload state
  aiImageFile = signal<File | null>(null);
  aiImagePreview = signal<string>(''); // Data URL for preview
  aiImageMode = signal<boolean>(false); // Toggle between text and image mode

  // Template content signal for reactive updates
  templateContentSignal = signal<string>('');
  // Subject content signal for reactive updates
  subjectContentSignal = signal<string>('');
  // Email background color extracted from HTML (for preview)
  emailBackgroundColor = signal<string>('');

  // NEW: Computed signals
  hasUnpublishedChanges = computed(() => {
    if (!this.emailInitial) return false;
    if (!this.emailInitial.publishedSnapshot) return true;
    return (
      this.currentContentHash() !==
      this.emailInitial.publishedSnapshot.contentHash
    );
  });

  isEnglishValid = computed(() => {
    const enValidation = this.languageValidationStates()['en'];
    return enValidation?.isValid ?? false;
  });

  canTranslate = computed(() => {
    return this.isEnglishValid() && !this.translateInProgress;
  });

  // Relative time since last translation
  lastTranslatedRelative = computed(() => {
    const timestamp = this.lastTranslatedAt();
    if (!timestamp) return null;

    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    return `${diffDay}d ago`;
  });

  // Relative time since last published
  publishedTimeAgo = computed(() => {
    if (!this.emailInitial?.publishedSnapshot?.publishedAt) return '';

    const timestamp = this.emailInitial.publishedSnapshot.publishedAt;
    // Handle Firestore Timestamp or Date
    const publishedDate =
      timestamp instanceof Date
        ? timestamp
        : (timestamp as any).toDate?.() || new Date(timestamp);

    const now = new Date();
    const diffMs = now.getTime() - publishedDate.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    if (diffWeek < 4) return `${diffWeek}w ago`;
    return `${diffMonth}mo ago`;
  });

  canPublish = computed(() => {
    if (!this.hasUnpublishedChanges()) return false;
    if (!this.isEnglishValid()) return false;

    const allValid = this.languageCodes().every((lang) => {
      const validation = this.languageValidationStates()[lang];
      return validation?.isValid ?? false;
    });

    return allValid;
  });

  // Can send test email when current language HTML is valid (no errors)
  canSendTestEmail = computed(() => {
    const currentValidation =
      this.languageValidationStates()[this.currLangCode()];
    return currentValidation?.isValid ?? false;
  });

  // ═════════════════════════════════════════════════════════════════════
  // Translation job state (subcollection emails/{id}/translations/{lang})
  // ═════════════════════════════════════════════════════════════════════

  /** Per-lang job docs from the Firestore subcollection. Updated live by onSnapshot. */
  translationStates = signal<{ [lang: string]: TranslationJobDoc }>({});

  /** SHA-256 hash of the current EN htmlPart + subjectPart in the form.
   * Recomputed whenever EN form values change (debounced).
   * Compared against translationStates[lang].sourceEnHash to derive 'stale'. */
  currentEnHash = signal<string>('');

  /** Maps each non-EN language to its current UI state (single source of truth
   * for buttons, color, retry visibility, tooltips). */
  langUiStates = computed<{ [lang: string]: LangUiState }>(() => {
    const states = this.translationStates();
    const enHash = this.currentEnHash();
    const out: { [lang: string]: LangUiState } = {};

    for (const lang of this.languageCodes()) {
      if (lang === 'en') continue;
      const doc = states[lang];
      const formContent = (this.templateGroup?.get(lang)?.value as string) || '';
      const formSubject = (this.subjectGroup?.get(lang)?.value as string) || '';
      const hasContent = formContent.trim().length > 0 || formSubject.trim().length > 0;

      if (!doc) {
        // No subcollection doc — empty if form also empty, else 'done-fresh' assumption
        // (covers existing emails that have content but were never tracked here;
        // migration seed will populate the doc shortly with a real sourceEnHash).
        out[lang] = hasContent ? 'done-fresh' : 'empty';
        continue;
      }

      switch (doc.status) {
        case 'queued':
        case 'translating': {
          // Tombstone protection: if a worker crashed without writing
          // done|failed AND Cloud Tasks retries are exhausted, the doc
          // can be stuck in queued/translating forever. After STUCK_AFTER_MS
          // we render it as 'failed' so the user sees the ↻ retry icon
          // instead of an infinite blue spinner.
          const updatedMs = this.docUpdatedMillis(doc);
          const ageMs = updatedMs ? Date.now() - updatedMs : 0;
          if (updatedMs && ageMs > STUCK_AFTER_MS) {
            out[lang] = 'failed';
          } else {
            out[lang] = doc.status;
          }
          break;
        }
        case 'failed':
          out[lang] = 'failed';
          break;
        case 'done':
          if (enHash && doc.sourceEnHash && doc.sourceEnHash !== enHash) {
            out[lang] = 'stale';
          } else {
            out[lang] = hasContent ? 'done-fresh' : 'empty';
          }
          break;
      }
    }
    return out;
  });

  /** Extract milliseconds-since-epoch from a Firestore Timestamp-or-Date. */
  private docUpdatedMillis(doc: TranslationJobDoc): number | null {
    const t = doc.updatedAt as any;
    if (!t) return null;
    if (typeof t.toMillis === 'function') return t.toMillis();
    if (typeof t.toDate === 'function') return t.toDate().getTime();
    if (t instanceof Date) return t.getTime();
    return null;
  }

  /** Languages that need translation work: empty, stale, or failed. */
  langsNeedingWork = computed<string[]>(() => {
    const ui = this.langUiStates();
    return Object.keys(ui).filter(
      (lang) => ui[lang] === 'empty' || ui[lang] === 'stale' || ui[lang] === 'failed'
    );
  });

  /** Languages currently in flight (queued or translating). */
  langsInFlight = computed<string[]>(() => {
    const ui = this.langUiStates();
    return Object.keys(ui).filter(
      (lang) => ui[lang] === 'queued' || ui[lang] === 'translating'
    );
  });

  /** Label for the smart Translate button. */
  translateButtonLabel = computed<string>(() => {
    if (this.langsInFlight().length > 0) {
      return `Translating ${this.langsInFlight().length}…`;
    }
    const n = this.langsNeedingWork().length;
    if (n === 0) return 'All translations up to date';
    if (n === 1) return 'Translate 1 language';
    return `Translate ${n} languages`;
  });

  /** Tooltip per per-lang status. */
  getLangTooltip(lang: string): string {
    const state = this.langUiStates()[lang];
    switch (state) {
      case 'empty': return 'No translation yet — click Translate.';
      case 'queued': return 'Queued for translation…';
      case 'translating': return 'Translating now…';
      case 'done-fresh': return 'Translation up to date.';
      case 'stale': return 'English changed since last translation. Click ↻ to refresh.';
      case 'failed': {
        const err = this.translationStates()[lang]?.errorMessage;
        return err ? `Translation failed: ${err}. Click ↻ to retry.` : 'Translation failed. Click ↻ to retry.';
      }
      default: return '';
    }
  }

  /** Whether the per-lang ↻ button should be shown. */
  canRetryLang(lang: string): boolean {
    if (this.translateInProgress) return false;
    const state = this.langUiStates()[lang];
    return state === 'stale' || state === 'failed';
  }

  /** Holds the most recent unsubscribe handles so we can clean up. */
  private translationsUnsub: (() => void) | null = null;
  /** Tracks the previous status per lang for auto-copy-to-form transition detection. */
  private lastSeenStatus: { [lang: string]: string } = {};
  /** Wall-clock ms of the user's most recent KEYSTROKE in each lang's
   * html or subject field. Used to suppress auto-copy of worker output
   * for ~5s after a real edit so we don't clobber the user mid-type.
   * Programmatic setValue from copyTranslationToForm doesn't update this
   * (gated by programmaticEdit flag below). */
  private lastUserEditAt: { [lang: string]: number } = {};
  /** True while we are programmatically setting a form value via
   * copyTranslationToForm. Suppresses lastUserEditAt updates so worker
   * writes don't self-suppress subsequent worker writes. */
  private programmaticEdit = false;

  testEmailBlockReason = computed(() => {
    const currentValidation =
      this.languageValidationStates()[this.currLangCode()];
    if (!currentValidation?.hasContent) {
      return 'Add HTML content to send test email';
    }
    if (!currentValidation?.isValid) {
      return 'Fix HTML validation errors before sending test email';
    }
    return '';
  });

  publishBlockReason = computed(() => {
    if (!this.hasUnpublishedChanges()) return 'No changes to publish';
    if (!this.isEnglishValid()) return 'English content has validation errors';

    const invalidLangs = this.languageCodes().filter((lang) => {
      const validation = this.languageValidationStates()[lang];
      return !(validation?.isValid ?? false);
    });

    if (invalidLangs.length > 0) {
      return `Validation errors in: ${invalidLangs
        .map((l) => l.toUpperCase())
        .join(', ')}`;
    }

    return '';
  });

  // Email-incompatible HTML elements and attributes
  private readonly emailIncompatibleElements = [
    'script',
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'button',
    'select',
    'textarea',
    'video',
    'audio',
    'canvas',
    'svg',
    'math',
  ];

  private readonly emailIncompatibleAttributes = [
    'onclick',
    'onload',
    'onerror',
    'onmouseover',
    'onmouseout',
    'onfocus',
    'onblur',
  ];

  // HTML beautify options
  private beautifyOptions = {
    indent_size: 2,
    indent_char: ' ',
    max_preserve_newlines: 2,
    preserve_newlines: true,
    indent_inner_html: true,
    wrap_line_length: 120,
    wrap_attributes: 'auto' as const,
    unformatted: ['code', 'pre', 'em', 'strong', 'span'],
    content_unformatted: ['pre', 'code'],
    extra_liners: ['head', 'body', '/html'],
  };

  codeMirrorOptions: any = {
    mode: 'htmlmixed',
    theme: 'material-darker',
    indentWithTabs: false,
    indentUnit: 2,
    smartIndent: true,
    lineNumbers: true,
    lineWrapping: true,
    extraKeys: {
      'Ctrl-Space': 'autocomplete',
      'Ctrl-J': 'toMatchingTag',
    },
    gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
    autoCloseBrackets: true,
    matchBrackets: true,
    matchTags: { bothTags: true },
    lint: false,
    autoCloseTags: true,
    tabSize: 2,
    foldGutter: true,
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    public cc: CacheService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private fbo: FirebaseOptimisticService,
    private fbs: FirebaseService,
    private alertService: AlertService,
    private router: Router,
    private emailService: EmailService,
    private snackBar: MatSnackBar,
    private storageService: StorageService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private store: FirestoreStoreService,
    private injector: Injector,
    private authService: AuthService,
    private afs: AngularFirestore
  ) {
    this.emailForm = this.initiateForm();
    this.testForm = this.fb.group({
      email: new FormControl('', [Validators.required, Validators.email]),
      langCode: new FormControl('en', Validators.required),
      variables: this.fb.array([]),
      templateVariables: this.fb.array([]),
    });
  }

  /**
   * Create the system-provided 'name' variable
   * This variable is mandatory and automatically available in all templates
   */
  private createSystemNameVariable(languageCodes: string[]): TemplateVariable {
    const value: { [langCode: string]: string | number } = {};
    languageCodes.forEach((code) => {
      value[code] = 'John Doe';
    });

    return {
      id: 'system-name-variable',
      name: 'name',
      value: value,
      isSystemVariable: true,
      createdAt: new Date(),
      createdBy: 'system',
    };
  }

  /**
   * Ensure system variables are present in the template variables array
   */
  private ensureSystemVariables(
    variables: TemplateVariable[],
    languageCodes: string[]
  ): TemplateVariable[] {
    // Check if the system 'name' variable already exists
    const hasSystemName = variables.some(
      (v) => v.name === 'name' && v.isSystemVariable
    );

    if (!hasSystemName) {
      // Add the system name variable at the beginning
      return [this.createSystemNameVariable(languageCodes), ...variables];
    }

    return variables;
  }

  /**
   * Ensure all template variables have entries for all active language codes.
   * When a new language is added, existing variables may be missing that language key.
   */
  private ensureVariableLanguageCoverage(variables: TemplateVariable[], langCodes: string[]): TemplateVariable[] {
    return variables.map(v => {
      const updatedValue = { ...v.value };
      let changed = false;
      for (const lang of langCodes) {
        if (!(lang in updatedValue)) {
          updatedValue[lang] = updatedValue['en'] ?? '';
          changed = true;
        }
      }
      return changed ? { ...v, value: updatedValue } : v;
    });
  }

  ngOnInit() {
    this.getEmailData();
  }

  ngAfterViewInit() {
    // Set up real-time validation on HTML content changes
    setTimeout(() => {
      this.setupRealtimeValidation();
      this.setupFormChangeTracking();
    }, 500);
    setTimeout(() => this.updateScrollArrows(), 600);
  }

  /**
   * Track form changes for unsaved changes warning
   */
  private setupFormChangeTracking() {
    this.emailForm.valueChanges
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(() => {
        this.hasUnsavedChanges.set(true);
      });
  }

  private setupRealtimeValidation() {
    // Subscribe to template control value changes for real-time validation
    if (this.templateGroup) {
      this.languageCodes().forEach((langCode) => {
        const control = this.templateGroup.get(langCode);
        if (control) {
          // Non-debounced edit tracker — fires synchronously on EVERY change.
          // We use this to record real user keystrokes so copyTranslationToForm
          // can suppress auto-copy when the user is actively editing.
          control.valueChanges
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(() => {
              if (!this.programmaticEdit) {
                this.lastUserEditAt[langCode] = Date.now();
              }
            });
          control.valueChanges
            .pipe(
              takeUntil(this.unSubscribe),
              debounceTime(300) // Debounce to prevent flickering - 300ms for smooth updates
            )
            .subscribe(async () => {
              // Only validate if this is the current language
              if (langCode === this.currLangCode()) {
                // Update signal for real-time preview
                this.templateContentSignal.set(control.value || '');
                // Update preview with debounced content
                this.updatePreview();
                await this.validateCurrentLanguage();
              }
            });
        }
      });
    }

    // Subscribe to subject control value changes for real-time subject display and validation
    if (this.subjectGroup) {
      this.languageCodes().forEach((langCode) => {
        const control = this.subjectGroup.get(langCode);
        if (control) {
          // Same edit-tracker as for html (subject edits also count)
          control.valueChanges
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(() => {
              if (!this.programmaticEdit) {
                this.lastUserEditAt[langCode] = Date.now();
              }
            });
          control.valueChanges
            .pipe(
              takeUntil(this.unSubscribe),
              debounceTime(300) // Debounce for smooth validation
            )
            .subscribe(async () => {
              if (langCode === this.currLangCode()) {
                this.subjectContentSignal.set(control.value || '');

                // Validate subject for undefined variables
                const subjectErrors = this.validateSubjectVariables(
                  control.value || ''
                );
                const errorMessages = subjectErrors.map((e) => e.message);
                this.subjectValidationErrors.set(errorMessages);

                // Re-validate current language to update overall state
                await this.validateCurrentLanguage();
              }
            });
        }
      });
    }

    // Also set up blur handler for formatting
    if (this.codemirrorComponent?.codeMirror) {
      this.codemirrorComponent.codeMirror.on('blur', () => {
        this.onEditorBlur();
      });
    }
  }

  private async validateCurrentLanguage() {
    const validation = await this.validateLanguage(this.currLangCode());
    const states = { ...this.languageValidationStates() };
    states[this.currLangCode()] = validation;
    this.languageValidationStates.set(states);

    // Also update htmlValidationErrors for instant error panel feedback
    const htmlValidationErrors: HtmlValidationError[] = validation.errors.map(
      (err) => ({
        message: err.message,
        type: err.severity,
        line: err.line,
      })
    );
    this.htmlValidationErrors.set(htmlValidationErrors);
    this.isHtmlValid.set(validation.isValid);
  }

  getEmailData() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/emails']);
      return;
    }

    // Use store's signal for real-time email data (replaces Firestore subscription)
    const emailSignal = this.store.getEmailById(id);

    // Helper function to initialize the editor with email data
    const initializeWithEmail = async (email: JsEmail) => {
      if (this.isInitialized) {
        // Already initialized - just update emailInitial for comparison
        this.emailInitial = email;
        return;
      }

      this.isInitialized = true;
      this.emailInitial = email;
      this.emailForm = this.initiateForm(email);
      this.initialVariables = [...email.templateVariables];
      this.getConfig();
      this.currLangCode.set(email.defaultLangCode);

      // Calculate initial hash
      const hash = await EmailHashUtil.generateContentHash(email);
      this.currentContentHash.set(hash);

      // Subscribe to live translation status from the subcollection.
      // Safe to do here even before form has lang controls — the subscription
      // just populates translationStates(); UI derivation handles missing form values.
      this.subscribeToTranslations(email.id);

      // EN-hash setup + migration seed are triggered from getConfig()'s
      // subscribe callback (see end of getConfig method) where the per-lang
      // FormControls are guaranteed populated. setTimeout-based deferral was
      // brittle because getConfig is async and its completion time varies.

      // Initialize validation states after config is loaded
      setTimeout(async () => {
        await this.validateAllLanguages();
      }, 500);
    };

    // Check immediately if email exists in the store
    const initialEmail = emailSignal();
    if (initialEmail) {
      // Email found immediately - initialize right away
      initializeWithEmail(initialEmail);
    }

    // Watch signal changes using effect for future updates
    effect(
      async () => {
        const res = emailSignal();
        if (res) {
          await initializeWithEmail(res);
        }
      },
      { allowSignalWrites: true, injector: this.injector }
    );

    // Safety timeout: if email not loaded after 5 seconds, show error and redirect
    setTimeout(() => {
      if (!this.isInitialized) {
        console.error(`[EmailEditor] Email ${id} not found after timeout`);
        this.snackBar.open('Email template not found. Please try again.', 'Close', {
          duration: 5000,
        });
        this.router.navigate(['/emails']);
      }
    }, 5000);
  }

  getConfig() {
    this.cc.config$.pipe(takeUntil(this.unSubscribe)).subscribe((res) => {
      if (res) {
        this.languageCodes.set(sortLanguageCodes(res.publishLanguageCodes));
        setTimeout(() => this.updateScrollArrows(), 100);
        if (this.languageCodes().length !== 0) {
          this.languageCodes().map((code) => {
            this.subjectGroup.addControl(
              code,
              new FormControl(
                this.emailInitial.subjectPart[code]
                  ? this.emailInitial.subjectPart[code]
                  : ''
              )
            );
            if (this.emailInitial.defaultLangCode === code) {
              this.subjectGroup.get(code)?.setValidators([Validators.required]);
              this.subjectGroup.get(code)?.updateValueAndValidity();
              this.lastTranslatedSubject =
                this.subjectGroup.value[this.emailInitial.defaultLangCode];
            }
            this.templateGroup.addControl(
              code,
              new FormControl(
                this.emailInitial.htmlPart[code]
                  ? this.emailInitial.htmlPart[code]
                  : ''
              )
            );
            if (this.emailInitial.defaultLangCode === code) {
              this.templateGroup
                .get(code)
                ?.setValidators([Validators.required]);
              this.templateGroup.get(code)?.updateValueAndValidity();
              this.lastTranslatedHtml =
                this.templateGroup.value[this.emailInitial.defaultLangCode];
            }
          });
        }

        // Ensure system variables (like 'name') are always present
        let updatedVariables = this.ensureSystemVariables(
          this.emailInitial.templateVariables,
          this.languageCodes()
        );
        // Backfill missing language codes in all template variables
        updatedVariables = this.ensureVariableLanguageCoverage(updatedVariables, this.languageCodes());
        this.emailForm.get('templateVariables')?.setValue(updatedVariables);
        this.emailInitial.templateVariables = updatedVariables;

        if (this.emailInitial.publishedSnapshot) {
          this.addTestTemplateVars();
        }

        // Initialize content signals with current values
        const defaultLang = this.emailInitial.defaultLangCode;
        this.templateContentSignal.set(
          this.emailInitial.htmlPart[defaultLang] || ''
        );
        this.subjectContentSignal.set(
          this.emailInitial.subjectPart[defaultLang] || ''
        );

        // Initialize preview with current content
        this.updatePreview();

        // Set up real-time validation after config is loaded and editor is ready
        setTimeout(async () => {
          this.setupRealtimeValidation();
          // Re-validate now that per-lang FormControls exist. The earlier
          // validateAllLanguages call from initializeWithEmail (setTimeout 500)
          // may race with getConfig and run before the controls are added,
          // leaving canTranslate=false. Running it here guarantees a valid state.
          await this.validateAllLanguages();

          // Translation pipeline: capture EN hash, watch for EN edits,
          // seed migration docs. All three depend on per-lang FormControls
          // existing, so they're triggered HERE (not in initializeWithEmail
          // where getConfig hasn't fired yet).
          await this.recomputeEnHash();
          this.setupEnHashWatcher();
          if (this.emailInitial?.id) {
            await this.seedTranslationDocsIfMissing(this.emailInitial.id);
          }
        }, 100);
      }
    });
  }

  /**
   * Opens a dialog to edit the template description
   */
  editDescription() {
    // Store the current description to detect changes
    const originalDescription = this.emailInitial.description;

    const dialogRef = this.dialog.open(EmailFormComponent, {
      width: '500px !important',
      maxWidth: '500px !important',
      maxHeight: '80vh',
      disableClose: true,
      data: {
        mode: 'edit',
        value: _.cloneDeep(this.emailInitial),
        hideEditButton: true, // Hide "Edit Template" button since we're already in the editor
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((result: JsEmail | undefined) => {
        // If the dialog was saved and description changed
        if (result && result.description !== originalDescription) {
          // Update the form control with the new description
          this.emailForm.patchValue({
            description: result.description,
          });

          // Update the initial value to reflect the change
          this.emailInitial.description = result.description;
        }
      });
  }

  /**
   * Opens a dialog to clone the current template
   */
  async cloneTemplate() {
    const dialogRef = this.dialog.open(EmailCloneDialogComponent, {
      width: '500px !important',
      maxWidth: '500px !important',
      maxHeight: '80vh',
      disableClose: true,
      data: {
        sourceTemplate: this.emailInitial,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(async (result: CloneDialogResult | undefined) => {
        if (!result) return;

        // 1. Check if template name already exists in AWS SES
        const templateNameForCheck = `${result.templateName}-${this.emailInitial.defaultLangCode}`;
        const existingTemplate = this.emailService
          .emailTemplates()
          .find((t: { Name: string }) => t.Name === templateNameForCheck);

        if (existingTemplate) {
          await this.alertService.confirm(
            'Template Name Exists',
            'A template with this name already exists in AWS SES. Please choose a different name.',
            'OK',
            'Cancel',
            false
          );
          return;
        }

        // 2. Check if template name exists in Firestore
        const existingInFirestore = this.store
          .activeEmails()
          .find((e) => e.templateName === result.templateName);

        if (existingInFirestore) {
          await this.alertService.confirm(
            'Template Name Exists',
            'A template with this name already exists in the database. Please choose a different name.',
            'OK',
            'Cancel',
            false
          );
          return;
        }

        // 3. Create cloned email object
        const clonedEmail: JsEmail = {
          id: getNewId(),
          templateName: result.templateName,
          description: result.description,
          htmlPart: _.cloneDeep(this.emailInitial.htmlPart),
          subjectPart: _.cloneDeep(this.emailInitial.subjectPart),
          templateVariables: _.cloneDeep(this.emailInitial.templateVariables),
          defaultLangCode: this.emailInitial.defaultLangCode,
          version: 1,
          publishedSnapshot: null,
          contentHash: '',
        };

        // 4. Calculate content hash
        clonedEmail.contentHash = await EmailHashUtil.generateContentHash(
          clonedEmail
        );

        // 5. Save to Firestore
        const success = await this.fbo.createItemsOptimistic<JsEmail>(
          [clonedEmail],
          'emails'
        );

        if (success) {
          this.snackBar
            .open(
              `Template "${result.templateName}" created successfully`,
              'View',
              { duration: 5000 }
            )
            .onAction()
            .subscribe(() => {
              this.router.navigate(['/email-editor', clonedEmail.id]);
            });

          // Navigate to the new template
          this.router.navigate(['/email-editor', clonedEmail.id]);
        }
      });
  }

  async deleteHandler() {
    if (this.emailInitial.publishedSnapshot) {
      const confirmed = await this.alertService.confirm(
        'Delete Published Template',
        'This template has been published. Deleting it will NOT remove it from AWS SES. Are you sure?',
        'Delete',
        'Cancel'
      );
      if (!confirmed) return;
    } else {
      const confirmed = await this.alertService.confirm(
        'Delete Template',
        'Are you sure you want to delete this template?'
      );
      if (!confirmed) return;
    }

    this.emailInitial.deletedAt = new Date();
    await this.fbo.updateItemsOptimistic<JsEmail>(
      [this.emailInitial],
      'emails'
    );
    this.router.navigate(['/emails']);
  }

  initiateForm(email?: JsEmail): FormGroup {
    return (this.emailForm = this.fb.group({
      id: new FormControl(email ? email.id : '', Validators.required),
      version: new FormControl(email ? email.version : 1, Validators.required),
      description: new FormControl(
        email ? email.description : '',
        Validators.required
      ),
      htmlPart: this.fb.group({}),
      subjectPart: this.fb.group({}),
      templateVariables: new FormControl(email ? email.templateVariables : []),
      defaultLangCode: new FormControl(
        email ? email.defaultLangCode : 'en',
        Validators.required
      ),
      templateName: new FormControl(email ? email.templateName : ''),
      publishedSnapshot: new FormControl(email?.publishedSnapshot || null),
      contentHash: new FormControl(email?.contentHash || ''),
    }));
  }

  openVariableDialog(mode: 'new' | 'edit', variable?: TemplateVariable) {
    if (this.variableDialogRef) {
      return;
    }

    this.variableDialogRef = this.dialog.open(VariableDialogComponent, {
      width: '350px !important',
      maxWidth: '350px !important',
      disableClose: true,
      hasBackdrop: false,
      position: {
        left: '30px',
        bottom: '60px',
      },
      data: new VariableDialogModel(
        mode,
        this.languageCodes(),
        variable || null
      ),
    });

    this.variableDialogRef
      .afterClosed()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        (
          data: {
            variable: TemplateVariable;
            mode: 'new' | 'edit' | 'delete';
          } | null
        ) => {
          if (data) {
            let variables = this.emailForm.value.templateVariables || [];
            switch (data.mode) {
              case 'new':
                variables.push(data.variable);
                break;

              case 'delete':
                variables = variables.filter(
                  (variable: TemplateVariable) =>
                    variable.id !== data.variable.id
                );
                break;

              default:
                variables = variables.map((variable: TemplateVariable) =>
                  variable.id === data.variable.id ? data.variable : variable
                );
                break;
            }
            this.emailForm.get('templateVariables')?.setValue(variables);
          }
          this.variableDialogRef = null;
        }
      );
  }

  getFc(fcName: string): FormControl {
    return this.emailForm.get(fcName) as FormControl;
  }

  getTestFc(fcName: string): FormControl {
    return this.testForm.get(fcName) as FormControl;
  }

  get subjectGroup(): FormGroup {
    return this.emailForm.get('subjectPart') as FormGroup;
  }

  get templateGroup(): FormGroup {
    return this.emailForm.get('htmlPart') as FormGroup;
  }

  get subjectControl(): FormControl {
    return this.emailInitial &&
      this.objectKeys(this.subjectGroup.controls).length
      ? (this.subjectGroup.get(this.currLangCode()) as FormControl)
      : new FormControl('');
  }

  get templateControl(): FormControl {
    return this.emailInitial &&
      this.objectKeys(this.templateGroup.controls).length
      ? (this.templateGroup.get(this.currLangCode()) as FormControl)
      : new FormControl('');
  }

  // Computed signal for reactive HTML template (for editor - respects toggle)
  htmlTemplate = computed(() => {
    // Use signal to trigger reactivity
    let html = this.templateContentSignal() || this.templateControl.value || '';

    // If showing variable values, replace {{key}} with actual values
    if (this.showVariableValues()) {
      html = this.replaceVariablesWithValues(html);
    }

    return html;
  });

  // Safe HTML signal for iframe preview - writable signal to control updates explicitly
  safePreviewHtml = signal<SafeHtml>('');

  // Track last rendered content to prevent unnecessary updates
  private _lastRenderedPreviewContent = '';

  /**
   * Build preview HTML content with variable replacement
   */
  private buildPreviewHtml(): string {
    let html = this.templateContentSignal() || this.templateControl.value || '';

    // Only replace variables when toggle is ON
    if (this.showVariableValues()) {
      html = this.replaceVariablesWithValues(html);
    }

    return html;
  }

  /**
   * Process email HTML to simulate Gmail's dark mode rendering
   */
  private processEmailForColorScheme(html: string, isDark: boolean, langCode?: string): string {
    const hasDoctype = html.trim().toLowerCase().startsWith('<!doctype');

    // RTL alignment preservation — only injected for RTL languages
    const rtlAlignmentStyles = langCode && isRtlLanguage(langCode) ? `
<style id="rtl-alignment-fix">
  [dir="rtl"] h1, [dir="rtl"] h2, [dir="rtl"] h3, [dir="rtl"] h4, [dir="rtl"] h5, [dir="rtl"] h6 {
    text-align: center !important;
  }
  [dir="rtl"] .footer, [dir="rtl"] [class*="footer"],
  [dir="rtl"] .footer td, [dir="rtl"] [class*="footer"] td,
  [dir="rtl"] .footer p, [dir="rtl"] [class*="footer"] p {
    text-align: center !important;
  }
</style>` : '';

    if (isDark) {
      const viewportMeta = '<meta name="viewport" content="width=device-width, initial-scale=1">';
      const gmailDarkModeStyles = `
${viewportMeta}
<style id="gmail-dark-mode-simulation">
  :root { color-scheme: dark !important; }
  html, body { background-color: #121212 !important; color: #e8eaed !important; margin: 0 !important; padding: 0 !important; }
  table { max-width: 100% !important; }
  body, td, th, p, div { word-wrap: break-word !important; overflow-wrap: break-word !important; }
  [style*="background-color: #fff"], [style*="background-color:#fff"],
  [style*="background-color: white"], [style*="background-color:white"],
  [style*="background: #fff"], [style*="background:#fff"],
  [bgcolor="#ffffff"], [bgcolor="#FFFFFF"], [bgcolor="white"] { background-color: #121212 !important; }
  [style*="background-color: #f"], [style*="background-color:#f"],
  [bgcolor^="#f"], [bgcolor^="#F"] { background-color: #1e1e1e !important; }
  [style*="color: #000"], [style*="color:#000"], [style*="color: black"], [style*="color:black"],
  [style*="color: #333"], [style*="color:#333"], [style*="color: #222"], [style*="color:#222"] { color: #e8eaed !important; }
  a { color: #8ab4f8 !important; }
  .email-container, .wrapper, .container, .content, .body-content,
  [class*="container"], [class*="wrapper"], [class*="content"] { background-color: #1e1e1e !important; color: #e8eaed !important; }
  .footer, [class*="footer"] { background-color: #121212 !important; color: #9aa0a6 !important; }
  p, h1, h2, h3, h4, h5, h6, span, li, td { color: #e8eaed !important; }
  .btn, .button, [class*="button"], [class*="btn"] { color: #ffffff !important; }
</style>`;

      const dirAttr = langCode && isRtlLanguage(langCode) ? ' dir="rtl"' : '';

      if (hasDoctype) {
        let result = html.replace(/<head[^>]*>/i, `$&${gmailDarkModeStyles}${rtlAlignmentStyles}`);
        if (dirAttr && !/<html[^>]*dir=/i.test(result)) {
          result = result.replace(/<html([^>]*)>/i, `<html$1${dirAttr}>`);
        }
        return result;
      } else {
        return `<!DOCTYPE html><html${dirAttr}><head><meta name="color-scheme" content="dark">${gmailDarkModeStyles}${rtlAlignmentStyles}</head><body>${html}</body></html>`;
      }
    } else {
      // Light mode: just ensure proper document structure without forcing background colors
      let processedHtml = html;

      // Remove @media (prefers-color-scheme: dark) blocks
      processedHtml = processedHtml.replace(
        /@media\s*\(\s*prefers-color-scheme\s*:\s*dark\s*\)\s*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/gi,
        ''
      );

      const viewportMeta = '<meta name="viewport" content="width=device-width, initial-scale=1">';
      const lightModeStyles = `
${viewportMeta}
<style id="light-mode-override">
  :root { color-scheme: light only !important; }
  html, body { margin: 0 !important; padding: 0 !important; }
  table { max-width: 100% !important; }
  body, td, th, p, div { word-wrap: break-word !important; overflow-wrap: break-word !important; }
</style>`;

      const dirAttr = langCode && isRtlLanguage(langCode) ? ' dir="rtl"' : '';

      if (hasDoctype) {
        let result = processedHtml.replace(/<head[^>]*>/i, `$&${lightModeStyles}${rtlAlignmentStyles}`);
        if (dirAttr && !/<html[^>]*dir=/i.test(result)) {
          result = result.replace(/<html([^>]*)>/i, `<html$1${dirAttr}>`);
        }
        return result;
      } else {
        return `<!DOCTYPE html><html${dirAttr}><head><meta name="color-scheme" content="light only">${lightModeStyles}${rtlAlignmentStyles}</head><body>${processedHtml}</body></html>`;
      }
    }
  }

  // Track last rendered state to prevent unnecessary updates
  private _lastRenderedDarkMode = false;
  private _lastRenderedLangCode = '';

  /**
   * Update preview - only if content, dark mode, or language has actually changed
   */
  updatePreview(): void {
    const newContent = this.buildPreviewHtml();
    const isDark = this.previewDarkMode();
    const langCode = this.currLangCode();

    if (
      newContent !== this._lastRenderedPreviewContent ||
      isDark !== this._lastRenderedDarkMode ||
      langCode !== this._lastRenderedLangCode
    ) {
      this._lastRenderedPreviewContent = newContent;
      this._lastRenderedDarkMode = isDark;
      this._lastRenderedLangCode = langCode;

      // Extract background color from the email content
      const bgColor = this.extractEmailBackgroundColor(newContent);
      this.emailBackgroundColor.set(bgColor);

      const processedHtml = this.processEmailForColorScheme(newContent, isDark, this.currLangCode());
      this.safePreviewHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(processedHtml)
      );
    }
  }

  // Computed signal for reactive subject display - shows resolved values based on toggle state
  displaySubject = computed(() => {
    const subject =
      this.subjectContentSignal() || this.subjectControl.value || '';
    // Only replace variables when toggle is ON
    if (this.showVariableValues()) {
      return this.replaceVariablesWithValues(subject);
    }
    return subject;
  });

  /**
   * Extract background color from email HTML content
   * Checks body tag, html tag, wrapper elements, and inline styles
   * Returns empty string if no background found (fallback will be used in CSS)
   */
  private extractEmailBackgroundColor(html: string): string {
    if (!html || !html.trim()) return '';

    // Try to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Priority order for finding background color:
    // 1. body tag bgcolor attribute
    // 2. body tag inline style background/background-color
    // 3. html tag bgcolor attribute
    // 4. html tag inline style
    // 5. First table with width="100%" (common email pattern)
    // 6. First element with background color style

    const body = doc.body;
    const htmlEl = doc.documentElement;

    // Check body bgcolor attribute
    const bodyBgColor = body?.getAttribute('bgcolor');
    if (bodyBgColor) {
      return this.normalizeColor(bodyBgColor);
    }

    // Check body inline style
    const bodyStyle = body?.getAttribute('style') || '';
    const bodyBgMatch = bodyStyle.match(/background(?:-color)?\s*:\s*([^;]+)/i);
    if (bodyBgMatch) {
      const color = bodyBgMatch[1].trim().split(' ')[0]; // Get first value (color, not image)
      if (color && !color.includes('url')) {
        return this.normalizeColor(color);
      }
    }

    // Check html element bgcolor
    const htmlBgColor = htmlEl?.getAttribute('bgcolor');
    if (htmlBgColor) {
      return this.normalizeColor(htmlBgColor);
    }

    // Check html inline style
    const htmlStyle = htmlEl?.getAttribute('style') || '';
    const htmlBgMatch = htmlStyle.match(/background(?:-color)?\s*:\s*([^;]+)/i);
    if (htmlBgMatch) {
      const color = htmlBgMatch[1].trim().split(' ')[0];
      if (color && !color.includes('url')) {
        return this.normalizeColor(color);
      }
    }

    // Check for wrapper table (common email pattern)
    const tables = doc.querySelectorAll('table');
    for (const table of Array.from(tables)) {
      const width = table.getAttribute('width');
      if (width === '100%' || table.style.width === '100%') {
        const tableBgColor = table.getAttribute('bgcolor');
        if (tableBgColor) {
          return this.normalizeColor(tableBgColor);
        }
        const tableStyle = table.getAttribute('style') || '';
        const tableBgMatch = tableStyle.match(
          /background(?:-color)?\s*:\s*([^;]+)/i
        );
        if (tableBgMatch) {
          const color = tableBgMatch[1].trim().split(' ')[0];
          if (color && !color.includes('url')) {
            return this.normalizeColor(color);
          }
        }
      }
    }

    // Check first div/section with background
    const containers = doc.querySelectorAll('div, section, td');
    for (const container of Array.from(containers)) {
      const bgColor = (container as HTMLElement).getAttribute('bgcolor');
      if (bgColor) {
        return this.normalizeColor(bgColor);
      }
      const style = container.getAttribute('style') || '';
      const bgMatch = style.match(/background(?:-color)?\s*:\s*([^;]+)/i);
      if (bgMatch) {
        const color = bgMatch[1].trim().split(' ')[0];
        if (
          color &&
          !color.includes('url') &&
          color !== 'transparent' &&
          color !== 'inherit'
        ) {
          return this.normalizeColor(color);
        }
      }
    }

    return '';
  }

  /**
   * Normalize color value to a consistent format
   */
  private normalizeColor(color: string): string {
    if (!color) return '';

    color = color.trim().toLowerCase();

    // Handle common color names
    const colorMap: { [key: string]: string } = {
      white: '#ffffff',
      black: '#000000',
      transparent: '',
      inherit: '',
    };

    if (colorMap[color] !== undefined) {
      return colorMap[color];
    }

    // Handle shorthand hex (#fff -> #ffffff)
    if (/^#[0-9a-f]{3}$/i.test(color)) {
      return (
        '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3]
      );
    }

    // Return as-is for valid hex, rgb, rgba, hsl values
    if (
      /^#[0-9a-f]{6}$/i.test(color) ||
      /^rgb/i.test(color) ||
      /^hsl/i.test(color)
    ) {
      return color;
    }

    // For other named colors, return as-is (browser will handle)
    return color;
  }

  /**
   * Replace {{variableName}} with actual values from templateVariables
   * Handles optional whitespace inside braces: {{name}}, {{ name }}, {{  name  }}
   */
  private replaceVariablesWithValues(text: string): string {
    const variables = this.emailForm.value.templateVariables || [];
    let result = text;

    variables.forEach((variable: TemplateVariable) => {
      // Allow optional whitespace inside braces: {{ name }} or {{name}}
      const pattern = new RegExp(`\\{\\{\\s*${variable.name}\\s*\\}\\}`, 'g');
      const value =
        variable.value[this.currLangCode()] ||
        variable.value['en'] ||
        `{{${variable.name}}}`;
      result = result.replace(pattern, String(value));
    });

    return result;
  }

  get testTemplateVars(): FormArray {
    return this.testForm.get('templateVariables') as FormArray;
  }

  get recipientVars(): FormArray {
    return this.testForm.get('variables') as FormArray;
  }

  get isTranslated(): boolean {
    const isTemplateTranslated = this.languageCodes().every(
      (lang) =>
        this.templateGroup.value[lang] &&
        this.templateGroup.value[lang]?.trim().length > 0
    );

    const isSubjectTranslated = this.languageCodes().every(
      (lang) =>
        this.subjectGroup.value[lang] &&
        this.subjectGroup.value[lang]?.trim().length > 0
    );

    const isTemplateEdited =
      this.templateGroup.value[this.emailInitial?.defaultLangCode] &&
      this.templateGroup.value[this.emailInitial?.defaultLangCode] ===
        this.lastTranslatedHtml;

    const isSubjectEdited =
      this.subjectGroup.value[this.emailInitial?.defaultLangCode] &&
      this.subjectGroup.value[this.emailInitial?.defaultLangCode] ===
        this.lastTranslatedSubject;

    return (
      isTemplateTranslated &&
      isSubjectTranslated &&
      isTemplateEdited &&
      isSubjectEdited
    );
  }

  getTempVarFc(i: number): FormControl {
    return this.testTemplateVars.controls[i]
      .get('value')
      ?.get(this.testForm.value.langCode) as FormControl;
  }

  addTestTemplateVars() {
    this.testTemplateVars.clear();
    this.emailInitial.templateVariables.map((variable) => {
      this.testTemplateVars.push(this.createVariable(variable));
    });
  }

  createVariable(variable?: TemplateVariable) {
    return this.fb.group({
      name: new FormControl(variable ? variable.name : '', Validators.required),
      value: this.addLangFc(variable?.value),
    });
  }

  addLangFc(value?: { [langCode: string]: string | number }): FormGroup {
    const langGroup = this.fb.group({});
    this.languageCodes().map((code) => {
      langGroup.addControl(
        code,
        new FormControl(value && value[code] ? value[code] : '')
      );
    });
    console.log('i am called lang groudddppp', langGroup);
    return langGroup;
  }

  addRecipientVariable() {
    this.recipientVars.push(this.createRecipientVariable());
  }

  createRecipientVariable() {
    return this.fb.group({
      name: new FormControl('', Validators.required),
      value: new FormControl('', Validators.required),
    });
  }

  removeRecipientVariable(index: number) {
    this.recipientVars.removeAt(index);
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj) as string[];
  }

  /** Check if current user is admin (can delete templates) */
  get isAdmin(): boolean {
    return this.authService.isAdmin;
  }

  hasChanges() {
    if (!this.emailInitial) return false;
    const isDescriptionChanged = !_.isEqual(
      this.emailInitial.description,
      this.emailForm.value.description
    );
    const isSubjectChanged = !_.isEqual(
      this.emailInitial.subjectPart,
      this.emailForm.value.subjectPart
    );
    const isHtmlChanged = !_.isEqual(
      this.emailInitial.htmlPart,
      this.emailForm.value.htmlPart
    );
    const isTemplateVariablesChanged = !this.areEqualVars(
      this.initialVariables,
      this.emailForm.value.templateVariables
    );
    return (
      isDescriptionChanged ||
      isSubjectChanged ||
      isHtmlChanged ||
      isTemplateVariablesChanged
    );
  }

  areEqualVars(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false;

    const sortFn = (x: any) => x.id;

    const sortedA = [...a].sort((x, y) => (sortFn(x) > sortFn(y) ? 1 : -1));
    const sortedB = [...b].sort((x, y) => (sortFn(x) > sortFn(y) ? 1 : -1));

    return JSON.stringify(sortedA) === JSON.stringify(sortedB);
  }

  /**
   * Format HTML content using js-beautify (fallback/basic formatting)
   */
  formatHtml(html: string): string {
    if (!html || !html.trim()) return html;
    return beautifyHtml(html, this.beautifyOptions);
  }

  /**
   * Format current language HTML content using AI for superior formatting
   * AI will deeply clean and optimize the HTML, removing unnecessary spaces, padding, tabs, etc.
   */
  async formatAllHtmlContent() {
    console.log('🎨 Format button clicked - starting AI formatting...');

    // Set loading state IMMEDIATELY for user feedback - ONLY for format button
    this.aiFormattingLoading.set(true);
    console.log('🎨 aiFormattingLoading set to TRUE');
    console.log(
      '🎨 aiAssistantLoading should stay:',
      this.aiAssistantLoading()
    );

    try {
      const currentContent = this.templateControl.value;

      // Validate we have content to format
      if (!currentContent || !currentContent.trim()) {
        console.warn('No content to format');
        this.snackBar.open('No content to format', 'Close', { duration: 2000 });
        return;
      }

      // Store current HTML for undo
      this.aiPreviousHtml.set(currentContent);
      console.log(
        `📝 Formatting ${
          currentContent.length
        } characters for language: ${this.currLangCode()}`
      );

      // Create comprehensive formatting instruction
      const formatInstruction = `Deep clean and format this HTML email template to make it perfect:

FORMATTING REQUIREMENTS:
1. Remove ALL unnecessary whitespace (spaces, tabs, newlines between tags)
2. Fix inconsistent indentation - use exactly 2 spaces per level
3. Remove redundant or duplicate inline styles
4. Consolidate and optimize CSS properties
5. Remove empty attributes and meaningless wrapper elements
6. Clean up table-based layouts - remove unnecessary rows/cells
7. Standardize attribute order: id, class, style, other attributes
8. Remove trailing spaces and empty lines
9. Ensure proper tag nesting and hierarchy
10. Optimize for email clients - keep essential inline styles only

PRESERVE:
- All variable placeholders in format {{variableName}}
- All functional HTML elements and content
- Critical inline styles needed for email rendering
- Image sources and links
- Email client compatibility features (tables, inline styles)

OUTPUT:
Return ONLY the perfectly formatted HTML with:
- Minimal whitespace
- Clean, consistent indentation
- Optimized inline styles
- Professional, production-ready code
- No markdown code blocks or explanations`;

      // Build context for AI
      const templateVariables = (
        this.emailForm.value.templateVariables || []
      ).map((v: any) => ({
        name: v.name,
        sampleValue: v.value?.[this.currLangCode()] || v.value?.['en'] || '',
        isSystemVariable: v.isSystemVariable || false,
      }));

      const payload = {
        htmlContent: currentContent,
        instruction: formatInstruction,
        context: {
          languageCode: this.currLangCode(),
          languageName: this.getLanguageName(this.currLangCode()),
          templateVariables: templateVariables,
          subject: this.subjectControl.value || '',
          templateName: this.emailForm.value.templateName || '',
          templateDescription: this.emailForm.value.description || '',
        },
      };

      console.log('🤖 Calling AI with payload...');

      // Call AI to format
      const res = (await this.fbs.callableFunction(
        'aiEditHtml',
        payload
      )) as any;

      console.log(
        '✅ AI response received:',
        res?.success ? 'SUCCESS' : 'FAILED'
      );

      if (res && res.success && res.data?.htmlContent) {
        // Apply basic beautify to ensure consistent formatting
        const formattedHtml = this.formatHtml(res.data.htmlContent);

        console.log(
          `📦 Formatted HTML length: ${formattedHtml.length} characters`
        );

        // Update the editor
        this.templateControl.setValue(formattedHtml);
        this.templateContentSignal.set(formattedHtml);
        this.updatePreview();

        // Enable undo
        this.aiCanUndo.set(true);

        // Re-validate the formatted HTML
        await this.validateCurrentLanguage();

        // Success message
        this.snackBar
          .open('✨ HTML formatted and optimized by AI', 'Undo', {
            duration: 8000,
          })
          .onAction()
          .subscribe(() => {
            this.undoAiChange();
          });

        console.log('✨ Formatting complete!');
      } else {
        console.warn(
          '⚠️ AI formatting failed, using basic formatting fallback'
        );
        // Fallback to basic formatting
        const basicFormatted = this.formatHtml(currentContent);
        this.templateControl.setValue(basicFormatted);

        this.snackBar.open('Using basic formatting (AI unavailable)', 'Close', {
          duration: 3000,
        });
      }
    } catch (error: any) {
      console.error('❌ AI formatting error:', error);

      // Fallback to basic formatting
      const currentContent = this.templateControl.value;
      if (currentContent && currentContent.trim()) {
        const formatted = this.formatHtml(currentContent);
        this.templateControl.setValue(formatted);
      }

      this.snackBar.open(
        'Error during AI formatting. Used basic formatting.',
        'Close',
        { duration: 3000 }
      );
    } finally {
      this.aiFormattingLoading.set(false);
      console.log('🎨 aiFormattingLoading set to FALSE');
      console.log('🏁 Formatting process ended');
    }
  }

  /**
   * Handle blur event from CodeMirror editor - only validate, don't format
   * Formatting should only happen when user explicitly requests it via the format button
   */
  onEditorBlur() {
    const currentContent = this.templateControl.value;
    if (currentContent && currentContent.trim()) {
      // Only validate the HTML - do NOT format automatically
      // This prevents content from "jumping back" when user manually arranges tags
      this.validateHtml(currentContent);
    } else {
      // Clear errors if empty
      this.htmlValidationErrors.set([]);
      this.isHtmlValid.set(true);
    }
  }

  /**
   * Validate HTML content for both syntax and email compatibility
   */
  validateHtml(html: string): boolean {
    const errors: HtmlValidationError[] = [];

    // Basic HTML structure validation
    const structureErrors = this.validateHtmlStructure(html);
    errors.push(...structureErrors);

    // Email compatibility validation
    const emailErrors = this.validateEmailCompatibility(html);
    errors.push(...emailErrors);

    // Update signals
    this.htmlValidationErrors.set(errors);
    this.isHtmlValid.set(errors.filter((e) => e.type === 'error').length === 0);

    return this.isHtmlValid();
  }

  /**
   * Validate basic HTML structure
   */
  private validateHtmlStructure(html: string): HtmlValidationError[] {
    const errors: HtmlValidationError[] = [];
    const lines = html.split('\n');

    // CRITICAL FIX: Check for incomplete/unclosed tags first (tags missing the closing >)
    // This catches malformed HTML like <table width="100%" that never closes
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineNum = i + 1;

      // Find all < characters that might start a tag
      let tagStartIndex = 0;
      while ((tagStartIndex = line.indexOf('<', tagStartIndex)) !== -1) {
        // Skip comments
        if (line.substring(tagStartIndex, tagStartIndex + 4) === '<!--') {
          const commentEnd = line.indexOf('-->', tagStartIndex);
          if (commentEnd === -1) {
            // Comment continues to next line or is incomplete
            tagStartIndex++;
            continue;
          }
          tagStartIndex = commentEnd + 3;
          continue;
        }

        // Find the next > to close this tag
        const tagEndIndex = line.indexOf('>', tagStartIndex);

        // If no > found on this line, check if it continues to next line
        if (tagEndIndex === -1) {
          // Look for > in subsequent lines (up to 3 lines ahead for multi-line tags)
          let foundClosing = false;
          let searchLines = 0;
          for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
            searchLines++;
            if (lines[j].includes('>')) {
              foundClosing = true;
              break;
            }
          }

          // If still no closing >, this is an incomplete tag
          if (!foundClosing) {
            // Extract tag name for better error message
            const tagMatch = line
              .substring(tagStartIndex)
              .match(/<\/?([a-zA-Z][a-zA-Z0-9]*)/);
            const tagName = tagMatch ? tagMatch[1] : 'unknown';

            errors.push({
              message: `Incomplete tag <${tagName}...> missing closing ">" character`,
              line: lineNum,
              type: 'error',
            });
          }
        }

        tagStartIndex++;
      }
    }

    // Check for unclosed tags using a simple stack-based approach
    const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*\/?>/g;
    const selfClosingTags = [
      'br',
      'hr',
      'img',
      'input',
      'meta',
      'link',
      'area',
      'base',
      'col',
      'embed',
      'param',
      'source',
      'track',
      'wbr',
    ];
    const tagStack: { tag: string; line: number }[] = [];

    let lineIndex = 0;
    let charCount = 0;

    let match;
    while ((match = tagPattern.exec(html)) !== null) {
      const fullMatch = match[0];
      const tagName = match[1].toLowerCase();
      const isClosing = fullMatch.startsWith('</');
      const isSelfClosing =
        fullMatch.endsWith('/>') || selfClosingTags.includes(tagName);

      // Calculate line number
      while (
        lineIndex < lines.length - 1 &&
        charCount + lines[lineIndex].length + 1 <= match.index
      ) {
        charCount += lines[lineIndex].length + 1;
        lineIndex++;
      }

      if (isSelfClosing) {
        continue;
      }

      if (!isClosing) {
        tagStack.push({ tag: tagName, line: lineIndex + 1 });
      } else {
        // Find matching opening tag
        let found = false;
        for (let i = tagStack.length - 1; i >= 0; i--) {
          if (tagStack[i].tag === tagName) {
            tagStack.splice(i, 1);
            found = true;
            break;
          }
        }
        if (!found) {
          errors.push({
            message: `Unexpected closing tag </${tagName}>`,
            line: lineIndex + 1,
            type: 'error',
          });
        }
      }
    }

    // Report unclosed tags
    for (const unclosed of tagStack) {
      errors.push({
        message: `Unclosed tag <${unclosed.tag}> (opened on line ${unclosed.line})`,
        line: unclosed.line,
        type: 'error',
      });
    }

    // Check for mismatched quotes in attributes
    const attrQuotePattern = /(\w+)=(['"])[^'"]*(?!\2)(?=['"])/g;
    const brokenAttrs = html.match(/\w+=["'][^"']*$/gm);
    if (brokenAttrs) {
      errors.push({
        message: `Mismatched quotes in attribute value`,
        type: 'error',
      });
    }

    // CSS Validation - check for CSS in <style> tags and inline styles
    const cssErrors = this.validateCss(html);
    errors.push(...cssErrors);

    // Variable Validation - check for malformed variable syntax
    const varErrors = this.validateVariableSyntax(html);
    errors.push(...varErrors);

    return errors;
  }

  /**
   * Validate CSS syntax (missing colons, semicolons, braces)
   */
  private validateCss(html: string): HtmlValidationError[] {
    const errors: HtmlValidationError[] = [];

    // Extract CSS from <style> tags
    const styleTagPattern = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    let styleMatch;
    while ((styleMatch = styleTagPattern.exec(html)) !== null) {
      const css = styleMatch[1];
      const cssErrors = this.checkCssSyntax(css);
      errors.push(...cssErrors);
    }

    // Check inline styles
    const inlineStylePattern = /style\s*=\s*["']([^"']*)["']/gi;
    let inlineMatch;
    while ((inlineMatch = inlineStylePattern.exec(html)) !== null) {
      const inlineCSS = inlineMatch[1];
      const inlineErrors = this.checkInlineStyleSyntax(inlineCSS);
      errors.push(...inlineErrors);
    }

    return errors;
  }

  /**
   * Check CSS block syntax
   */
  private checkCssSyntax(css: string): HtmlValidationError[] {
    const errors: HtmlValidationError[] = [];

    // Check for unmatched braces
    let braceCount = 0;
    for (let i = 0; i < css.length; i++) {
      if (css[i] === '{') braceCount++;
      if (css[i] === '}') braceCount--;
      if (braceCount < 0) {
        errors.push({
          message: 'Unmatched closing brace } in CSS',
          type: 'error',
        });
        break;
      }
    }
    if (braceCount > 0) {
      errors.push({
        message: 'Unclosed brace { in CSS',
        type: 'error',
      });
    }

    // Check for missing colons and semicolons in CSS rules
    const rulePattern = /([^{]*)\{([^}]*)\}/g;
    let match;
    while ((match = rulePattern.exec(css)) !== null) {
      const selector = match[1].trim();
      const block = match[2].trim();

      if (!block) continue;

      // Split by semicolon to get individual declarations
      const parts = block.split(';');

      // Check each declaration
      for (let i = 0; i < parts.length; i++) {
        const decl = parts[i].trim();
        if (!decl) continue;

        // Check for missing colon
        if (!decl.includes(':')) {
          errors.push({
            message: `Missing colon in CSS property: "${decl}"`,
            type: 'error',
          });
        }

        // Check for missing semicolon (if not the last declaration and block doesn't end with semicolon)
        // A declaration is missing a semicolon if:
        // 1. It's not the last item in the split array, OR
        // 2. The original block ends with content (not a semicolon) and this is the last item
        if (i < parts.length - 1 || (block.endsWith(decl) && decl.length > 0)) {
          // If this is the last part and there are multiple declarations
          if (
            i === parts.length - 1 &&
            parts.length > 1 &&
            !block.trim().endsWith(';')
          ) {
            errors.push({
              message: `Missing semicolon after CSS property: "${decl}"`,
              type: 'error',
            });
          }
        }
      }

      // Additional check: if block has content but no semicolon at all
      if (block && !block.includes(';') && block.includes(':')) {
        const trimmedBlock = block.trim();
        if (trimmedBlock && !trimmedBlock.endsWith(';')) {
          errors.push({
            message: `Missing semicolon at end of CSS declaration: "${trimmedBlock}"`,
            type: 'error',
          });
        }
      }
    }

    return errors;
  }

  /**
   * Check inline style syntax
   */
  private checkInlineStyleSyntax(style: string): HtmlValidationError[] {
    const errors: HtmlValidationError[] = [];
    const trimmedStyle = style.trim();

    if (!trimmedStyle) return errors;

    // Split by semicolon
    const parts = trimmedStyle.split(';');

    for (let i = 0; i < parts.length; i++) {
      const decl = parts[i].trim();
      if (!decl) continue;

      // Check for missing colon
      if (!decl.includes(':')) {
        errors.push({
          message: `Missing colon in inline style: "${decl}"`,
          type: 'error',
        });
      }
    }

    // Check for missing semicolon between multiple declarations
    // If there are multiple properties and style doesn't end with semicolon
    if (parts.length > 1 && !trimmedStyle.endsWith(';')) {
      const lastDecl = parts[parts.length - 1].trim();
      if (lastDecl && lastDecl.includes(':')) {
        errors.push({
          message: `Missing semicolon after inline style: "${lastDecl}"`,
          type: 'error',
        });
      }
    }

    return errors;
  }

  /**
   * Validate variable syntax {{variableName}}
   * The correct format is {{variableName}} with double braces
   */
  private validateVariableSyntax(html: string): HtmlValidationError[] {
    const errors: HtmlValidationError[] = [];
    const lines = html.split('\n');

    // Check for missing opening braces - pattern like {companyName}}
    const missingOpeningPattern = /(?<!\{)\{(?!\{)([^{}]+)\}\}/g;
    let match;
    while ((match = missingOpeningPattern.exec(html)) !== null) {
      const lineNumber = this.getLineNumber(html, match.index);
      errors.push({
        message: `Missing opening brace: "{${match[1]}}" should be "{{${match[1]}}"`,
        type: 'error',
        line: lineNumber,
      });
    }

    // Check for missing closing braces - pattern like {{companyName}
    const missingClosingPattern = /\{\{([^{}]+)\}(?!\})/g;
    while ((match = missingClosingPattern.exec(html)) !== null) {
      const lineNumber = this.getLineNumber(html, match.index);
      errors.push({
        message: `Missing closing brace: "{{${match[1]}}" should be "{{${match[1]}}}"`,
        type: 'error',
        line: lineNumber,
      });
    }

    // Check for completely unclosed variables like {{companyName (missing both closing braces)
    const unclosedPattern = /\{\{[^}]*$/gm;
    let lineNum = 1;
    for (const line of lines) {
      if (unclosedPattern.test(line)) {
        errors.push({
          message: 'Unclosed variable syntax - missing }}',
          type: 'error',
          line: lineNum,
        });
      }
      lineNum++;
    }

    return errors;
  }

  /**
   * Get line number from string index
   */
  private getLineNumber(text: string, index: number): number {
    const substring = text.substring(0, index);
    return substring.split('\n').length;
  }

  /**
   * Validate HTML for email client compatibility
   */
  private validateEmailCompatibility(html: string): HtmlValidationError[] {
    const errors: HtmlValidationError[] = [];
    const lowerHtml = html.toLowerCase();

    // Check for incompatible elements
    for (const element of this.emailIncompatibleElements) {
      const regex = new RegExp(`<${element}[\\s>]`, 'gi');
      if (regex.test(html)) {
        errors.push({
          message: `<${element}> tag is not supported in most email clients`,
          type: 'warning',
        });
      }
    }

    // Check for incompatible attributes (event handlers)
    for (const attr of this.emailIncompatibleAttributes) {
      const regex = new RegExp(`\\s${attr}\\s*=`, 'gi');
      if (regex.test(html)) {
        errors.push({
          message: `"${attr}" attribute is not supported in email clients`,
          type: 'warning',
        });
      }
    }

    // Check for external stylesheets (not supported in most email clients)
    if (/<link[^>]*rel=["']stylesheet["'][^>]*>/gi.test(html)) {
      errors.push({
        message:
          'External stylesheets (<link rel="stylesheet">) are not supported in most email clients. Use inline styles instead.',
        type: 'warning',
      });
    }

    // Check for position:fixed or position:absolute (limited support)
    if (/position\s*:\s*(fixed|absolute)/gi.test(html)) {
      errors.push({
        message:
          'CSS position:fixed and position:absolute have limited support in email clients',
        type: 'warning',
      });
    }

    // Check for flexbox (limited support)
    if (/display\s*:\s*flex/gi.test(html)) {
      errors.push({
        message:
          'CSS flexbox has limited support in email clients. Consider using tables for layout.',
        type: 'warning',
      });
    }

    // Check for grid (limited support)
    if (/display\s*:\s*grid/gi.test(html)) {
      errors.push({
        message:
          'CSS grid has limited support in email clients. Consider using tables for layout.',
        type: 'warning',
      });
    }

    return errors;
  }

  /**
   * Get error count by type
   */
  getErrorCount(): number {
    return this.htmlValidationErrors().filter((e) => e.type === 'error').length;
  }

  /**
   * Get warning count
   */
  getWarningCount(): number {
    return this.htmlValidationErrors().filter((e) => e.type === 'warning')
      .length;
  }

  /**
   * Jump to a specific line in the CodeMirror editor
   */
  jumpToLine(lineNumber: number): void {
    if (this.codemirrorComponent?.codeMirror) {
      const editor = this.codemirrorComponent.codeMirror;

      // Focus the editor
      editor.focus();

      // Move cursor to the specified line (CodeMirror uses 0-based line numbers)
      const line = Math.max(0, lineNumber - 1);

      // Set cursor position to the beginning of the line
      editor.setCursor({ line, ch: 0 });

      // Scroll the line into view
      editor.scrollIntoView({ line, ch: 0 }, 100);

      // Optional: Select the entire line to highlight it
      const lineContent = editor.getLine(line);
      if (lineContent) {
        editor.setSelection({ line, ch: 0 }, { line, ch: lineContent.length });
      }
    }
  }

  /**
   * Auto-fix validation errors using AI
   */
  async autoFixValidationErrors(): Promise<void> {
    console.log('🔧 Auto-fix validation button clicked');

    const errors = this.htmlValidationErrors();

    // If no errors detected by the basic validator, ask user if they want AI to do a deeper check
    if (errors.length === 0) {
      const confirmed = await this.alertService.confirm(
        'Run AI Validation Check?',
        'No obvious validation errors detected. Would you like to run an AI-powered deep validation check? The AI may catch subtle issues that the basic validator missed.',
        'Run AI Check',
        'Cancel'
      );

      if (!confirmed) {
        return;
      }

      // User wants AI to check anyway - let it proceed without specific errors
      console.log(
        '🔧 User requested AI validation check despite no detected errors'
      );
    }

    // Store current HTML for undo
    this.aiPreviousHtml.set(this.templateControl.value);

    // Set loading state IMMEDIATELY - ONLY for validation fix button
    this.aiAssistantLoading.set(true);
    console.log('🔧 aiAssistantLoading set to TRUE');
    console.log(
      '🔧 aiFormattingLoading should stay:',
      this.aiFormattingLoading()
    );

    // Build a detailed error report for the AI
    let fixPrompt: string;

    if (errors.length > 0) {
      // We have specific errors - list them
      const errorReport = errors
        .map((error, index) => {
          let errorText = `${index + 1}. [${error.type.toUpperCase()}]`;
          if (error.line) {
            errorText += ` Line ${error.line}:`;
          }
          errorText += ` ${error.message}`;
          return errorText;
        })
        .join('\n');

      fixPrompt = `Fix the following HTML validation errors and warnings in this email template:

VALIDATION ISSUES:
${errorReport}

IMPORTANT INSTRUCTIONS:
- Fix ALL errors and warnings listed above
- Maintain the existing HTML structure and content
- Preserve all variable placeholders in the format {{variableName}}
- Ensure email client compatibility (use inline styles, table-based layouts)
- Keep all existing functionality intact
- Return ONLY the corrected HTML without any explanations or markdown code blocks`;
    } else {
      // No errors detected by basic validator - do a deep AI check
      fixPrompt = `Perform a comprehensive validation check on this HTML email template and fix any issues you find:

DEEP VALIDATION CHECK - Look for:
- Incomplete or malformed HTML tags (missing closing >, unclosed tags, etc.)
- Mismatched or unclosed quotes in attributes
- Invalid HTML structure or nesting
- Email compatibility issues (unsupported CSS, missing inline styles, etc.)
- Broken variable placeholders or template syntax
- CSS syntax errors (missing semicolons, colons, braces, etc.)
- Any other issues that might cause rendering problems

IMPORTANT INSTRUCTIONS:
- Thoroughly examine the HTML for subtle issues that might have been missed
- Fix any problems you discover
- Maintain the existing HTML structure and content
- Preserve all variable placeholders in the format {{variableName}}
- Ensure email client compatibility (use inline styles, table-based layouts)
- Keep all existing functionality intact
- Return ONLY the corrected HTML without any explanations or markdown code blocks
- If the HTML is already valid, return it unchanged`;
    }

    try {
      // Build context information for AI
      const templateVariables = (
        this.emailForm.value.templateVariables || []
      ).map((v: any) => ({
        name: v.name,
        sampleValue: v.value?.[this.currLangCode()] || v.value?.['en'] || '',
        isSystemVariable: v.isSystemVariable || false,
      }));

      const payload = {
        htmlContent: this.templateControl.value,
        instruction: fixPrompt,
        context: {
          languageCode: this.currLangCode(),
          languageName: this.getLanguageName(this.currLangCode()),
          templateVariables: templateVariables,
          subject: this.subjectControl.value || '',
          templateName: this.emailForm.value.templateName || '',
          templateDescription: this.emailForm.value.description || '',
          validationErrors: errors.map((e) => ({
            type: e.type,
            message: e.message,
            line: e.line,
          })),
        },
      };

      const res = (await this.fbs.callableFunction(
        'aiEditHtml',
        payload
      )) as any;

      if (res && res.success && res.data?.htmlContent) {
        // Format the returned HTML
        const formattedHtml = this.formatHtml(res.data.htmlContent);

        // Update the editor
        this.templateControl.setValue(formattedHtml);
        this.templateContentSignal.set(formattedHtml);
        this.updatePreview();

        // Enable undo
        this.aiCanUndo.set(true);

        // Re-validate the fixed HTML
        await this.validateCurrentLanguage();

        // Check if errors were actually fixed
        const remainingErrors = this.htmlValidationErrors();
        const errorCount = remainingErrors.filter(
          (e) => e.type === 'error'
        ).length;
        const warningCount = remainingErrors.filter(
          (e) => e.type === 'warning'
        ).length;

        if (remainingErrors.length === 0) {
          this.snackBar
            .open('✨ All validation issues fixed!', 'Undo', { duration: 8000 })
            .onAction()
            .subscribe(() => {
              this.undoAiChange();
            });
        } else if (remainingErrors.length < errors.length) {
          this.snackBar
            .open(
              `✨ Fixed some issues. Remaining: ${errorCount} error(s), ${warningCount} warning(s)`,
              'Undo',
              { duration: 8000 }
            )
            .onAction()
            .subscribe(() => {
              this.undoAiChange();
            });
        } else {
          this.snackBar
            .open('AI attempted fixes. Please review changes.', 'Undo', {
              duration: 8000,
            })
            .onAction()
            .subscribe(() => {
              this.undoAiChange();
            });
        }
      } else {
        this.snackBar.open(
          res?.message || 'AI validation fix failed',
          'Close',
          { duration: 5000 }
        );
      }
    } catch (error: any) {
      console.error('AI validation fix error:', error);
      this.snackBar.open('Failed to apply AI validation fixes', 'Close', {
        duration: 5000,
      });
    } finally {
      this.aiAssistantLoading.set(false);
      console.log('🔧 aiAssistantLoading set to FALSE');
      console.log('🔧 Auto-fix validation process ended');
    }
  }

  async save() {
    if (!this.emailForm.valid) return;
    if (!this.hasChanges()) return;
    if (this.isSaving()) return; // Prevent double saves

    this.isSaving.set(true);

    try {
      // Validate all languages (do NOT auto-format - formatting should only happen when user clicks format button)
      await this.validateAllLanguages();

      // Calculate new hash
      const newHash = await EmailHashUtil.generateContentHash(
        this.emailForm.value
      );

      // Prepare update data
      const updateData: Partial<JsEmail> = {
        ...this.emailForm.value,
        contentHash: newHash,
        updatedAt: new Date(),
      };

      // Save to Firestore
      const emailBackup = _.cloneDeep(this.emailForm.value);
      const updateStatus = await this.fbo.updateItemsOptimistic<JsEmail>(
        [updateData as JsEmail],
        'emails'
      );

      if (updateStatus) {
        this.emailInitial = { ...this.emailInitial, ...updateData };
        this.initialVariables = [...this.emailInitial.templateVariables];
        this.currentContentHash.set(newHash);

        // Reset unsaved changes flag after successful save
        this.hasUnsavedChanges.set(false);

        this.snackBar.open('Draft saved', 'Close', { duration: 2000 });
      } else {
        this.emailInitial = _.cloneDeep(emailBackup);
        this.initialVariables = [...this.emailInitial.templateVariables];
      }
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Revert all changes to the last saved state
   */
  async revert() {
    if (!this.emailInitial) return;

    const confirmed = await this.alertService.confirm(
      'Revert Changes',
      'Are you sure you want to revert all unsaved changes? This cannot be undone.',
      'Revert',
      'Cancel'
    );

    if (!confirmed) return;

    // Reset the form to the initial values
    this.emailForm.patchValue({
      description: this.emailInitial.description,
      templateVariables: _.cloneDeep(this.emailInitial.templateVariables),
    });

    // Reset all language-specific content
    this.languageCodes().forEach((langCode) => {
      this.subjectGroup
        .get(langCode)
        ?.setValue(this.emailInitial.subjectPart[langCode] || '');
      this.templateGroup
        .get(langCode)
        ?.setValue(this.emailInitial.htmlPart[langCode] || '');
    });

    // Reset variables to initial state
    this.initialVariables = [...this.emailInitial.templateVariables];

    // Update signals to reflect reverted state
    const currentLang = this.currLangCode();
    this.templateContentSignal.set(
      this.emailInitial.htmlPart[currentLang] || ''
    );
    this.subjectContentSignal.set(
      this.emailInitial.subjectPart[currentLang] || ''
    );

    // Re-validate all languages
    await this.validateAllLanguages();

    // Update preview
    this.updatePreview();

    // Reset unsaved changes flag
    this.hasUnsavedChanges.set(false);

    this.snackBar.open('Changes reverted', 'Close', { duration: 2000 });
  }

  translationTargetCount = 0;

  /**
   * Recompute the SHA-256 hash of the current EN htmlPart + subjectPart.
   * Called whenever EN form fields change or on email init. The hash is used
   * to compare against per-lang translationStates[lang].sourceEnHash for
   * stale-detection in the UI.
   */
  private async recomputeEnHash(): Promise<void> {
    const enHtml = (this.templateGroup?.get('en')?.value as string) || '';
    const enSubject = (this.subjectGroup?.get('en')?.value as string) || '';
    const hash = await EmailHashUtil.hashEnSource(enHtml, enSubject);
    this.currentEnHash.set(hash);
  }

  /**
   * Watch EN form controls so the EN hash signal stays current. Debounced
   * 300ms to avoid thrashing on every keystroke. The recompute itself is
   * very cheap (one SHA-256), but recompute also re-derives langUiStates
   * which re-renders the language buttons.
   */
  private setupEnHashWatcher(): void {
    const enHtml = this.templateGroup?.get('en');
    const enSubject = this.subjectGroup?.get('en');
    if (!enHtml || !enSubject) return;

    enHtml.valueChanges.pipe(takeUntil(this.unSubscribe), debounceTime(300)).subscribe(() => {
      this.recomputeEnHash();
    });
    enSubject.valueChanges.pipe(takeUntil(this.unSubscribe), debounceTime(300)).subscribe(() => {
      this.recomputeEnHash();
    });
  }

  /**
   * Subscribe to live updates of the emails/{emailId}/translations subcollection.
   * Each per-lang status doc transitions queued → translating → done | failed.
   * Two side effects: update translationStates signal, and on transition to
   * 'done' auto-copy the translated content into the form.
   */
  private subscribeToTranslations(emailId: string): void {
    // Tear down any previous listener (defensive — initializeWithEmail short-circuits
    // re-init normally, but guard against double-subscription on hot reload)
    this.translationsUnsub?.();

    this.translationsUnsub = this.afs.firestore
      .collection('emails').doc(emailId).collection('translations')
      .onSnapshot((snapshot) => {
        const next: { [lang: string]: TranslationJobDoc } = { ...this.translationStates() };
        for (const change of snapshot.docChanges()) {
          const lang = change.doc.id;
          if (change.type === 'removed') {
            delete next[lang];
            continue;
          }
          const data = change.doc.data() as TranslationJobDoc;
          next[lang] = data;

          // Auto-copy to form on transition to 'done'
          const previousStatus = this.lastSeenStatus[lang];
          if (data.status === 'done' && previousStatus !== 'done') {
            this.copyTranslationToForm(lang, data.htmlPart, data.subjectPart);
          }
          this.lastSeenStatus[lang] = data.status;
        }
        this.translationStates.set(next);
      }, (err) => {
        console.error('[email-editor] translations subscription error:', err);
      });
  }

  /**
   * Copy a worker-produced translation into the form controls.
   *
   * Idempotent: if the form already has identical content, skip entirely
   * (no setValue, no dirty flag). This matters for two cases:
   *   1. Initial snapshot delivery — pre-existing 'done' docs would otherwise
   *      cause every existing lang to be marked dirty on every page load.
   *   2. Re-translation that produces same output — silently no-op.
   *
   * When content does change, marks the form dirty so the user knows there's
   * something to save and updates the overall content hash.
   */
  private copyTranslationToForm(lang: string, htmlPart: string, subjectPart: string): void {
    if (lang === 'en') return; // safety
    const tplCtrl = this.templateGroup?.get(lang);
    const subjCtrl = this.subjectGroup?.get(lang);
    if (!tplCtrl || !subjCtrl) return;

    const currentHtml = (tplCtrl.value as string) || '';
    const currentSubject = (subjCtrl.value as string) || '';
    const noChange = currentHtml === htmlPart && currentSubject === subjectPart;
    if (noChange) return;

    // Suppress auto-copy if the user has typed in this lang's field within
    // the last few seconds — they may be mid-edit and don't want the worker
    // to clobber their keystrokes. The translation is still persisted in the
    // subcollection; user can click ↻ on the (now stale-vs-EN) lang to refresh.
    const lastEdit = this.lastUserEditAt[lang] || 0;
    if (lastEdit && Date.now() - lastEdit < USER_EDIT_SUPPRESS_MS) {
      console.log(`[email-editor] Skipping auto-copy for ${lang}: user edit ${Date.now() - lastEdit}ms ago`);
      return;
    }

    // Mark setValue as programmatic so the lastUserEditAt tracker ignores it.
    this.programmaticEdit = true;
    try {
      tplCtrl.setValue(htmlPart);
      subjCtrl.setValue(subjectPart);
      tplCtrl.markAsDirty();
      subjCtrl.markAsDirty();
    } finally {
      this.programmaticEdit = false;
    }

    // Update overall content hash so unsaved-changes flag reflects new content
    EmailHashUtil.generateContentHash(this.emailForm.value).then((h) => {
      this.currentContentHash.set(h);
    });

    // Re-validate this lang
    this.validateLanguage(lang).then((validation) => {
      const states = { ...this.languageValidationStates() };
      states[lang] = validation;
      this.languageValidationStates.set(states);
    });
  }

  /**
   * Migration: for existing emails whose htmlPart[lang] already has content
   * but no subcollection doc exists, write a 'done' doc seeded with the
   * current EN hash. Best-effort: assumes existing translation matches
   * current EN. If not, the doc shows as 'stale' immediately and user
   * retranslates.
   */
  private async seedTranslationDocsIfMissing(emailId: string): Promise<void> {
    const enHash = this.currentEnHash();
    if (!enHash) return; // wait for hash to be computed

    const existing = this.translationStates();
    const seeds: Promise<any>[] = [];

    for (const lang of this.languageCodes()) {
      if (lang === 'en') continue;
      if (existing[lang]) continue; // already has a doc — nothing to do
      const html = (this.templateGroup?.get(lang)?.value as string) || '';
      const subject = (this.subjectGroup?.get(lang)?.value as string) || '';
      if (!html.trim() && !subject.trim()) continue; // genuinely empty

      // Seed: status=done, sourceEnHash=current EN hash
      seeds.push(
        this.afs.firestore
          .collection('emails').doc(emailId).collection('translations').doc(lang)
          .set({
            status: 'done',
            htmlPart: html,
            subjectPart: subject,
            sourceEnHash: enHash,
            jobId: 'migration-seed',
            createdAt: new Date(),
            updatedAt: new Date(),
          }, { merge: true })
      );
    }

    if (seeds.length > 0) {
      try {
        await Promise.all(seeds);
        console.log(`[email-editor] Seeded ${seeds.length} translation docs for ${emailId}`);
      } catch (err) {
        console.error('[email-editor] Failed to seed translation docs:', err);
      }
    }
  }

  /**
   * Single entry point for triggering translation. Used by both the smart
   * "Translate N languages" button (passes langsNeedingWork) and the per-lang
   * retry button (passes one lang). Returns immediately after enqueue;
   * UI updates are driven by the subcollection subscription.
   */
  async runTranslation(langs: string[]): Promise<void> {
    if (!this.canTranslate()) {
      this.snackBar.open('Please fix English content errors before translating', 'Close', { duration: 3000 });
      return;
    }
    if (!this.emailInitial?.id) {
      this.snackBar.open('Email not loaded', 'Close', { duration: 3000 });
      return;
    }
    if (langs.length === 0) {
      this.snackBar.open('All translations are already up to date', 'Close', { duration: 2000 });
      return;
    }

    this.translateInProgress = true;
    try {
      const payload: EnqueueTranslateRequest = {
        emailId: this.emailInitial.id,
        langs,
        htmlPart: (this.templateGroup.get('en')?.value as string) || '',
        subjectPart: (this.subjectGroup.get('en')?.value as string) || '',
      };
      this.translationTargetCount = langs.length;

      const res = (await this.fbs.callableFunction('translateTemplate', payload)) as EnqueueTranslateResponse;

      if (res?.success) {
        this.lastTranslatedAt.set(new Date());
        const enqueued = res.enqueuedLangs?.length ?? langs.length;
        const requested = langs.length;
        if (enqueued < requested) {
          this.snackBar.open(
            `Enqueued ${enqueued}/${requested} languages. Check status icons.`,
            'Close',
            { duration: 5000 }
          );
        } else if (requested === 1) {
          this.snackBar.open(`Translating ${getLanguageName(langs[0])}…`, 'Close', { duration: 2000 });
        } else {
          this.snackBar.open(`Translating ${requested} languages…`, 'Close', { duration: 2000 });
        }
      } else {
        this.snackBar.open(res?.message || 'Failed to enqueue translation', 'Close', { duration: 4000 });
      }
    } catch (error) {
      console.error('Enqueue translation failed:', error);
      this.snackBar.open('Failed to enqueue translation', 'Close', { duration: 3000 });
    } finally {
      this.translateInProgress = false;
    }
  }

  /**
   * Smart "Translate" button handler. Translates only the languages that
   * need work (empty + stale + failed). Skips fresh ones to avoid wasted calls.
   */
  async translateTemplate(): Promise<void> {
    await this.runTranslation(this.langsNeedingWork());
  }

  /**
   * Per-lang retry handler. Triggered by the ↻ icon on stale/failed langs.
   */
  async retryLang(lang: string): Promise<void> {
    await this.runTranslation([lang]);
  }

  async templateHandler(funcName: 'createTemplates' | 'updateTemplates') {
    const payload = {
      htmlPart: this.emailForm.value.htmlPart,
      subjectPart: this.emailForm.value.subjectPart,
      templateName: this.emailForm.value.templateName,
      languageCodes: this.languageCodes(),
    };
    const res = (await this.fbs.callableFunction(funcName, payload)) as any;
    if (res) {
      console.log(`${funcName} result:`, res);

      // Check per-language status for partial failures
      if (res.perLanguageStatus) {
        const failed = Object.entries(res.perLanguageStatus)
          .filter(([_, status]) => status === 'failed')
          .map(([lang]) => lang);

        if (failed.length > 0) {
          console.warn(`SES template ${funcName} failed for: ${failed.join(', ')}`);
          this.snackBar.open(
            `Warning: SES template failed for ${failed.join(', ')}. Other languages succeeded.`,
            'Close',
            { duration: 8000, panelClass: ['snackbar-error'] }
          );
        }
      }

      if (res.success === false) {
        throw new Error(res.message || `${funcName} failed`);
      }
    }
  }

  getTemplate() {
    this.fbs
      .getTemplate({ templateName: 'email_new_en_1' })
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((res) => {
        console.log('i am called res-->>', res);
      });
  }

  sendEmail() {
    if (this.sendingEmail) return;

    const recipientEmail = this.testForm.value.email;
    const emailRequest: any = {
      recipient: {
        email: recipientEmail,
        variables: this.getRecipientVariables(),
        langCode: this.testForm.value.langCode,
      },
      templateName: `${this.emailInitial.templateName}-${this.testForm.value.langCode}`,
      templateVariables: this.testForm.value.templateVariables,
      defaultLangCode: this.emailInitial.defaultLangCode,
      version: this.emailInitial.version,
    };

    this.sendingEmail = true;

    this.fbs
      .sendEmail(emailRequest)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe({
        next: (res: any) => {
          this.sendingEmail = false;
          console.log('Email send response:', res);

          // Check for success (MessageId indicates AWS SES accepted the email)
          if (res && res.MessageId) {
            this.snackBar.open(
              `✓ Test email sent successfully to ${recipientEmail}`,
              'Close',
              {
                duration: 5000,
                panelClass: ['snackbar-success'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              }
            );
          } else if (res && res.status === 'error') {
            // Error response from backend
            this.snackBar.open(
              `✗ Failed to send email: ${res.message || 'Unknown error'}`,
              'Close',
              {
                duration: 8000,
                panelClass: ['snackbar-error'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              }
            );
          } else {
            // Unexpected response format
            this.snackBar.open(
              `⚠ Email request completed but status uncertain`,
              'Close',
              {
                duration: 5000,
                panelClass: ['snackbar-warning'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              }
            );
          }
        },
        error: (error: any) => {
          this.sendingEmail = false;
          console.error('Email send error:', error);

          let errorMessage = 'Failed to send email';
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          } else if (error.status === 0) {
            errorMessage = 'Network error - please check your connection';
          } else if (error.status >= 500) {
            errorMessage = 'Server error - please try again later';
          }

          this.snackBar.open(`✗ ${errorMessage}`, 'Close', {
            duration: 8000,
            panelClass: ['snackbar-error'],
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        },
      });
  }

  resetTestForm() {
    this.testForm.reset();
    this.etestForm.resetForm();
  }

  getRecipientVariables(): { [varName: string]: string | number } {
    const variables: { [varName: string]: string | number } = {};
    const recipientVars = this.testForm.value.variables || [];
    recipientVars.map((v: { name: string; value: string | number }) => {
      variables[v.name] = v.value;
    });
    return variables;
  }

  /**
   * Open test email dialog
   */
  async openTestEmailDialog() {
    if (!this.emailInitial) return;

    // Get current user email for pre-fill
    const currentUserEmail = this.storageService.getUser()?.email || '';

    // Get current draft content from form (not published snapshot)
    const currentHtmlPart = this.emailForm.value.htmlPart;
    const currentSubjectPart = this.emailForm.value.subjectPart;

    // Prepare languages with validation states based on current draft content (sorted: English first, then alphabetical)
    const sortedLangCodes = sortLanguageCodes(this.languageCodes());
    const languagesWithValidation = await Promise.all(
      sortedLangCodes.map(async (langCode) => {
        // Check if current draft has content for this language
        const htmlContent = currentHtmlPart?.[langCode] ?? '';
        const subjectContent = currentSubjectPart?.[langCode] ?? '';
        const hasHtml = htmlContent.trim().length > 0;
        const hasSubject = subjectContent.trim().length > 0;
        const hasContent = hasHtml && hasSubject;

        // Validate the language content for errors
        const validation = await this.validateLanguage(langCode);
        const hasErrors =
          validation.errors.filter((e) => e.severity === 'error').length > 0;

        // A language is valid if it has content and no errors
        const isValid = hasContent && !hasErrors;

        return {
          languageCode: langCode,
          languageName: this.getLanguageName(langCode),
          isValid,
          hasContent,
        };
      })
    );

    // Find the first valid language, defaulting to 'en' if it's valid
    const defaultLang = this.emailForm.value.defaultLangCode || 'en';
    const defaultLangValid = languagesWithValidation.find(
      (l) => l.languageCode === defaultLang
    )?.isValid;
    const selectedLangCode = defaultLangValid
      ? defaultLang
      : languagesWithValidation.find((l) => l.isValid)?.languageCode ||
        defaultLang;

    this.dialog.open(TestEmailDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      panelClass: 'test-email-dialog-panel',
      data: {
        templateName: this.emailForm.value.templateName,
        languages: languagesWithValidation,
        templateVariables: this.emailForm.value.templateVariables || [],
        defaultEmail: currentUserEmail,
        defaultLangCode: selectedLangCode,
        // Always use raw HTML content from editor (not AWS SES template)
        source: 'editor' as const,
        htmlPart: currentHtmlPart,
        subjectPart: currentSubjectPart,
      },
    });
  }

  /**
   * Validate template variables in HTML
   */
  validateTemplateVariables(htmlContent: string): ValidationError[] {
    const errors: ValidationError[] = [];
    const variables = this.emailForm.value.templateVariables || [];

    const variablePattern = /\{\{(\w+)\}\}/g;
    let match;
    while ((match = variablePattern.exec(htmlContent)) !== null) {
      const varName = match[1];
      const isDefined = variables.some((v: any) => v.name === varName);
      if (!isDefined) {
        errors.push({
          type: 'variable',
          severity: 'error',
          message: `Variable "{{${varName}}}" is used but not defined`,
        });
      }
    }

    return errors;
  }

  /**
   * Validate template variables in subject line
   */
  validateSubjectVariables(subjectContent: string): ValidationError[] {
    const errors: ValidationError[] = [];
    const variables = this.emailForm.value.templateVariables || [];

    const variablePattern = /\{\{(\w+)\}\}/g;
    let match;
    const undefinedVars = new Set<string>();

    while ((match = variablePattern.exec(subjectContent)) !== null) {
      const varName = match[1];
      const isDefined = variables.some((v: any) => v.name === varName);
      if (!isDefined) {
        undefinedVars.add(varName);
      }
    }

    undefinedVars.forEach((varName) => {
      errors.push({
        type: 'variable',
        severity: 'error',
        message: `Variable "{{${varName}}}" is used but not defined`,
      });
    });

    return errors;
  }

  /**
   * Validate a single language
   */
  async validateLanguage(langCode: string): Promise<LanguageValidation> {
    const htmlContent = this.emailForm.value.htmlPart[langCode] || '';
    const subjectContent = this.emailForm.value.subjectPart[langCode] || '';

    const errors: ValidationError[] = [];
    const hasContent =
      htmlContent.trim().length > 0 && subjectContent.trim().length > 0;

    if (hasContent) {
      // HTML structure validation
      const structureErrors = this.validateHtmlStructure(htmlContent);
      for (const err of structureErrors) {
        errors.push({
          type: 'html',
          severity: err.type,
          message: err.message,
          line: err.line,
        });
      }

      // HTML variable validation
      const htmlVariableErrors = this.validateTemplateVariables(htmlContent);
      errors.push(...htmlVariableErrors);

      // Subject variable validation
      const subjectVariableErrors =
        this.validateSubjectVariables(subjectContent);
      // Prefix subject errors to distinguish them in the error panel
      const prefixedSubjectErrors = subjectVariableErrors.map((err) => ({
        ...err,
        message: `[Subject] ${err.message}`,
      }));
      errors.push(...prefixedSubjectErrors);
    }

    const hasErrors = errors.filter((e) => e.severity === 'error').length > 0;

    return {
      isValid: hasContent && !hasErrors,
      hasContent,
      errors,
    };
  }

  /**
   * Validate all languages
   */
  async validateAllLanguages(): Promise<boolean> {
    const states: { [lang: string]: LanguageValidation } = {};
    let allValid = true;

    for (const langCode of this.languageCodes()) {
      const validation = await this.validateLanguage(langCode);
      states[langCode] = validation;
      if (!validation.isValid) allValid = false;
    }

    this.languageValidationStates.set(states);

    const currentLangValidation = states[this.currLangCode()];
    if (currentLangValidation) {
      this.htmlValidationErrors.set(
        currentLangValidation.errors.map((e) => ({
          message: e.message,
          line: e.line,
          type: e.severity,
        }))
      );
      this.isHtmlValid.set(
        currentLangValidation.errors.filter((e) => e.severity === 'error')
          .length === 0
      );
    }

    return allValid;
  }

  /**
   * Check if a language is enabled for clicking
   * All languages should be clickable to allow users to switch and see the state
   * The actual editing capability depends on whether content exists
   */
  isLanguageEnabled(langCode: string): boolean {
    // All languages are always clickable
    return true;
  }

  /**
   * Check if a language has content
   */
  languageHasContent(langCode: string): boolean {
    const htmlContent = this.emailForm?.value?.htmlPart?.[langCode] || '';
    const subjectContent = this.emailForm?.value?.subjectPart?.[langCode] || '';
    return htmlContent.trim().length > 0 || subjectContent.trim().length > 0;
  }

  /**
   * Get validation tooltip for language button
   */
  getValidationTooltip(langCode: string): string {
    const validation = this.languageValidationStates()[langCode];
    if (!validation) return '';

    const errors = validation.errors.filter((e) => e.severity === 'error');
    if (errors.length === 0) return 'Valid';

    return errors.map((e) => e.message).join('\n');
  }

  /**
   * Get language name from code
   */
  getLanguageName(code: string): string {
    const lang = this.languages.find((l) => l.code === code);
    return lang?.name || code.toUpperCase();
  }

  /**
   * Check if a language code is RTL (for template binding)
   */
  isRtlLanguage(code: string): boolean {
    return isRtlLanguage(code);
  }

  /**
   * Switch language - always allowed, UI shows translation pending state if no content
   */
  async switchLanguage(langCode: string) {
    // Validate current language before switching
    const currentValidation = await this.validateLanguage(this.currLangCode());
    this.languageValidationStates.update((states) => ({
      ...states,
      [this.currLangCode()]: currentValidation,
    }));

    // Switch to the new language
    this.currLangCode.set(langCode);

    // Update template content signal for real-time preview
    const newControl = this.templateGroup.get(langCode);
    if (newControl) {
      this.templateContentSignal.set(newControl.value || '');
    }

    // Update subject content signal for real-time preview
    const newSubjectControl = this.subjectGroup.get(langCode);
    if (newSubjectControl) {
      this.subjectContentSignal.set(newSubjectControl.value || '');
    }

    // Update preview with new language content
    this.updatePreview();

    // Update validation errors panel for new language
    const newLangValidation = this.languageValidationStates()[langCode];
    if (newLangValidation) {
      this.htmlValidationErrors.set(
        newLangValidation.errors.map((e) => ({
          message: e.message,
          line: e.line,
          type: e.severity,
        }))
      );
    } else {
      // Clear errors if no validation state exists yet
      this.htmlValidationErrors.set([]);
    }
  }

  scrollLanguages(direction: 'left' | 'right') {
    const container = this.langButtonsContainer?.nativeElement;
    if (!container) return;
    const scrollAmount = 200;
    container.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    setTimeout(() => this.updateScrollArrows(), 300);
  }

  private updateScrollArrows() {
    const container = this.langButtonsContainer?.nativeElement;
    if (!container) {
      this.canScrollLeft.set(false);
      this.canScrollRight.set(false);
      return;
    }
    this.canScrollLeft.set(container.scrollLeft > 0);
    this.canScrollRight.set(container.scrollLeft + container.clientWidth < container.scrollWidth - 1);
  }

  /**
   * Toggle between showing variable keys {{key}} and values (sample data)
   */
  toggleVariableDisplay() {
    this.showVariableValues.update((value) => !value);
    this.updatePreview();
  }

  // ==================== AI ASSISTANT METHODS ====================

  /**
   * Toggle AI assistant popup
   */
  toggleAiAssistant() {
    this.aiAssistantOpen.update((v) => !v);
    if (this.aiAssistantOpen()) {
      this.aiInstruction.set('');
      this.clearAiImage();
    }
  }

  /**
   * Close AI assistant popup
   */
  closeAiAssistant() {
    this.aiAssistantOpen.set(false);
    this.aiInstruction.set('');
    this.clearAiImage();
  }

  /**
   * Toggle between text instruction and image upload mode
   */
  toggleAiImageMode() {
    this.aiImageMode.update((v) => !v);
    if (!this.aiImageMode()) {
      this.clearAiImage();
    }
  }

  /**
   * Handle image file selection
   */
  onAiImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      this.snackBar.open(
        'Please select a PNG, JPEG, WebP, or GIF image',
        'Close',
        { duration: 4000 }
      );
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.snackBar.open('Image is too large. Maximum size is 10MB', 'Close', {
        duration: 4000,
      });
      return;
    }

    this.aiImageFile.set(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      this.aiImagePreview.set(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  /**
   * Handle drag and drop for image upload
   */
  onAiImageDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      this.snackBar.open(
        'Please drop a PNG, JPEG, WebP, or GIF image',
        'Close',
        { duration: 4000 }
      );
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.snackBar.open('Image is too large. Maximum size is 10MB', 'Close', {
        duration: 4000,
      });
      return;
    }

    this.aiImageFile.set(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      this.aiImagePreview.set(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  /**
   * Prevent default drag behavior
   */
  onAiImageDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Clear selected image
   */
  clearAiImage() {
    this.aiImageFile.set(null);
    this.aiImagePreview.set('');
    this.aiImageMode.set(false);
  }

  /**
   * Process image with AI based on user instruction
   * Supports: design mockups, error screenshots, visual references
   */
  async submitAiImage() {
    const file = this.aiImageFile();
    const instruction = this.aiInstruction().trim();

    if (!file) {
      this.snackBar.open('Please select an image first', 'Close', {
        duration: 3000,
      });
      return;
    }

    if (!instruction) {
      this.snackBar.open(
        'Please describe what you want to do with this image',
        'Close',
        { duration: 3000 }
      );
      return;
    }

    this.aiAssistantLoading.set(true);

    try {
      // Store current HTML for undo
      const currentHtml = this.templateControl.value || '';
      this.aiPreviousHtml.set(currentHtml);

      // Convert file to base64
      const base64 = await this.fileToBase64(file);

      // Build context information
      const templateVariables = (
        this.emailForm.value.templateVariables || []
      ).map((v: any) => ({
        name: v.name,
        sampleValue: v.value[this.currLangCode()] || v.value['en'] || '',
        isSystemVariable: v.isSystemVariable || false,
      }));

      const payload = {
        imageBase64: base64,
        mimeType: file.type,
        instruction: instruction,
        currentHtml: currentHtml || undefined, // Pass current HTML for modification scenarios
        context: {
          languageCode: this.currLangCode(),
          languageName: this.getLanguageName(this.currLangCode()),
          templateVariables: templateVariables,
          templateName: this.emailForm.value.templateName || '',
          templateDescription: this.emailForm.value.description || '',
        },
      };

      const res = (await this.fbs.callableFunction(
        'aiGenerateFromImage',
        payload
      )) as any;

      if (res && res.success && res.data?.htmlContent) {
        // Format the returned HTML
        const formattedHtml = this.formatHtml(res.data.htmlContent);

        // Update the editor
        this.templateControl.setValue(formattedHtml);
        this.templateContentSignal.set(formattedHtml);
        this.updatePreview();

        // Enable undo
        this.aiCanUndo.set(true);

        // Clear and close
        this.aiInstruction.set('');
        this.clearAiImage();
        this.aiAssistantOpen.set(false);

        this.snackBar
          .open('HTML updated by AI', 'Undo', {
            duration: 8000,
          })
          .onAction()
          .subscribe(() => {
            this.undoAiChange();
          });

        // Re-validate
        await this.validateCurrentLanguage();
      } else {
        this.snackBar.open(res?.message || 'Failed to process image', 'Close', {
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('AI image processing error:', error);
      this.snackBar.open('Failed to process image', 'Close', {
        duration: 5000,
      });
    } finally {
      this.aiAssistantLoading.set(false);
    }
  }

  /**
   * Convert File to base64 string (without data URI prefix)
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URI prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Update AI instruction from input
   */
  onAiInstructionChange(event: Event) {
    const input = event.target as HTMLTextAreaElement;
    this.aiInstruction.set(input.value);
  }

  /**
   * Submit AI instruction to modify HTML
   */
  async submitAiInstruction() {
    const instruction = this.aiInstruction().trim();
    if (!instruction) return;

    const currentHtml = this.templateControl.value || '';
    if (!currentHtml.trim()) {
      this.snackBar.open('Add some HTML content first', 'Close', {
        duration: 3000,
      });
      return;
    }

    this.aiAssistantLoading.set(true);

    try {
      // Store current HTML for undo
      this.aiPreviousHtml.set(currentHtml);

      // Build context information for Gemini
      const templateVariables = (
        this.emailForm.value.templateVariables || []
      ).map((v: any) => ({
        name: v.name,
        sampleValue: v.value[this.currLangCode()] || v.value['en'] || '',
        isSystemVariable: v.isSystemVariable || false,
      }));

      const payload = {
        htmlContent: currentHtml,
        instruction: instruction,
        context: {
          languageCode: this.currLangCode(),
          languageName: this.getLanguageName(this.currLangCode()),
          templateVariables: templateVariables,
          subject: this.subjectControl.value || '',
          templateName: this.emailForm.value.templateName || '',
          templateDescription: this.emailForm.value.description || '',
        },
      };

      const res = (await this.fbs.callableFunction(
        'aiEditHtml',
        payload
      )) as any;

      if (res && res.success && res.data?.htmlContent) {
        // Format the returned HTML
        const formattedHtml = this.formatHtml(res.data.htmlContent);

        // Update the editor
        this.templateControl.setValue(formattedHtml);
        this.templateContentSignal.set(formattedHtml);
        this.updatePreview();

        // Enable undo
        this.aiCanUndo.set(true);

        // Clear instruction and close popup
        this.aiInstruction.set('');
        this.aiAssistantOpen.set(false);

        this.snackBar
          .open('✨ HTML updated by AI', 'Undo', {
            duration: 8000,
          })
          .onAction()
          .subscribe(() => {
            this.undoAiChange();
          });

        // Re-validate
        await this.validateCurrentLanguage();
      } else {
        this.snackBar.open(res?.message || 'AI edit failed', 'Close', {
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('AI edit error:', error);
      this.snackBar.open('Failed to apply AI changes', 'Close', {
        duration: 5000,
      });
    } finally {
      this.aiAssistantLoading.set(false);
    }
  }

  /**
   * Undo the last AI change
   */
  undoAiChange() {
    const previousHtml = this.aiPreviousHtml();
    if (!previousHtml) return;

    this.templateControl.setValue(previousHtml);
    this.templateContentSignal.set(previousHtml);
    this.updatePreview();
    this.aiCanUndo.set(false);
    this.aiPreviousHtml.set('');

    this.snackBar.open('AI change reverted', 'Close', { duration: 3000 });

    // Re-validate
    this.validateCurrentLanguage();
  }

  /**
   * Handle keyboard shortcuts in AI input
   */
  onAiKeydown(event: KeyboardEvent) {
    // Submit on Cmd/Ctrl + Enter
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.submitAiInstruction();
    }
    // Close on Escape
    if (event.key === 'Escape') {
      this.closeAiAssistant();
    }
  }

  /**
   * Set preview view mode (desktop/mobile)
   */
  setPreviewViewMode(mode: 'desktop' | 'mobile') {
    this.previewViewMode.set(mode);
  }

  /**
   * Toggle preview dark mode
   */
  togglePreviewDarkMode() {
    this.previewDarkMode.update((value) => !value);
    this.updatePreview();
  }

  /**
   * Toggle fullscreen preview mode using CDK Overlay
   */
  togglePreviewFullscreen() {
    if (this.previewFullscreen()) {
      this.closeFullscreenPreview();
    } else {
      this.openFullscreenPreview();
    }
  }

  /**
   * Open fullscreen preview using CDK Overlay
   */
  private openFullscreenPreview() {
    if (this.fullscreenOverlayRef) {
      return; // Already open
    }

    // Create overlay config for fullscreen
    const overlayConfig = this.overlay.create({
      hasBackdrop: false,
      positionStrategy: this.overlay.position().global(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: '100vw',
      height: '100vh',
    });

    this.fullscreenOverlayRef = overlayConfig;

    // Create and attach the template portal
    const portal = new TemplatePortal(
      this.fullscreenTemplate,
      this.viewContainerRef
    );
    this.fullscreenOverlayRef.attach(portal);

    this.previewFullscreen.set(true);
  }

  /**
   * Close fullscreen preview
   */
  closeFullscreenPreview() {
    if (this.fullscreenOverlayRef) {
      this.fullscreenOverlayRef.dispose();
      this.fullscreenOverlayRef = null;
    }
    this.previewFullscreen.set(false);
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy() {
    this.closeFullscreenPreview();
    this.translationsUnsub?.();
    this.translationsUnsub = null;
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  /**
   * Update custom preview dimensions
   */
  updatePreviewDimensions(width: number, height: number) {
    this.previewCustomWidth.set(width);
    this.previewCustomHeight.set(height);
  }

  /**
   * Can deactivate guard - check for unsaved changes
   */
  canDeactivate(): boolean {
    if (this.hasUnsavedChanges()) {
      return confirm(
        'You have unsaved changes. Are you sure you want to leave this page? All unsaved changes will be lost.'
      );
    }
    return true;
  }

  /**
   * Warn user before closing/refreshing with unsaved changes
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      $event.preventDefault();
      $event.returnValue = true;
    }
  }

  /**
   * Publish template to AWS SES
   */
  async publish() {
    if (!this.canPublish()) {
      this.snackBar.open(this.publishBlockReason(), 'Close', {
        duration: 5000,
      });
      return;
    }

    const allValid = await this.validateAllLanguages();
    if (!allValid) {
      this.snackBar.open(
        'Validation failed. Please fix all errors before publishing.',
        'Close',
        { duration: 5000 }
      );
      return;
    }

    const confirmed = await this.alertService.confirm(
      'Publish Template',
      'This will publish the template to AWS SES. All translations will be sent. Continue?',
      'Publish',
      'Cancel'
    );

    if (!confirmed) return;

    this.isPublishing.set(true);

    try {
      // Ensure all template variables have entries for all active languages before publishing
      const coveredVars = this.ensureVariableLanguageCoverage(
        this.emailForm.value.templateVariables || [],
        this.languageCodes()
      );
      this.emailForm.get('templateVariables')?.setValue(coveredVars);

      const contentHash = await EmailHashUtil.generateContentHash(
        this.emailForm.value
      );

      const snapshot: PublishedSnapshot = {
        htmlPart: { ...this.emailForm.value.htmlPart },
        subjectPart: { ...this.emailForm.value.subjectPart },
        templateVariables: [...coveredVars],
        description: this.emailForm.value.description,
        publishedAt: new Date(),
        publishedBy: '',
        contentHash: contentHash,
      };

      const updateData: Partial<JsEmail> = {
        ...this.emailForm.value,
        publishedSnapshot: snapshot,
        contentHash: contentHash,
        version: this.emailForm.value.version + 1,
        updatedAt: new Date(),
      };

      await this.fbo.updateItemsOptimistic<JsEmail>(
        [updateData as JsEmail],
        'emails'
      );

      // Always use updateTemplates — backend handles upsert (create-if-not-exists) per language
      await this.templateHandler('updateTemplates');

      this.emailInitial = { ...this.emailInitial, ...updateData };
      this.initialVariables = [...this.emailInitial.templateVariables];
      this.currentContentHash.set(contentHash);
      this.addTestTemplateVars();

      // Reset unsaved changes flag after successful publish
      this.hasUnsavedChanges.set(false);

      this.snackBar.open('✓ Template published successfully!', 'Close', {
        duration: 5000,
        panelClass: ['snackbar-success'],
      });
    } catch (error) {
      console.error('Publish failed:', error);
      this.snackBar.open('Publication failed. Please try again.', 'Close', {
        duration: 5000,
        panelClass: ['snackbar-error'],
      });
    } finally {
      this.isPublishing.set(false);
    }
  }

  /**
   * Open diff comparison dialog
   */
  openDiffDialog(): void {
    if (!this.emailInitial.publishedSnapshot) return;

    const dialogData: EmailDiffDialogData = {
      templateName: this.emailInitial.templateName,
      draft: {
        htmlPart: this.emailForm.value.htmlPart,
        subjectPart: this.emailForm.value.subjectPart,
        templateVariables: this.emailForm.value.templateVariables,
        description: this.emailForm.value.description,
      },
      published: this.emailInitial.publishedSnapshot,
      languageCodes: this.languageCodes(),
      defaultLangCode: this.emailInitial.defaultLangCode,
    };

    const dialogRef = this.dialog.open(EmailDiffDialogComponent, {
      width: '95vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: 'diff-dialog-panel',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === 'revert') {
        this.revertToPublished();
      }
    });
  }

  /**
   * View published version preview
   * Shows the published version for the specified or currently selected language
   */
  viewPublishedVersion(langCode?: string): void {
    if (!this.emailInitial.publishedSnapshot) return;

    const previewLangCode = langCode || this.currLangCode();
    const publishedSnapshot = this.emailInitial.publishedSnapshot;

    // Build ALL language options for test email dialog (sorted: English first, then alphabetical)
    const allLanguageCodes = sortLanguageCodes(this.languageCodes());
    const languages = allLanguageCodes.map((lc) => ({
      languageCode: lc,
      languageName: this.getLanguageName(lc),
      isValid: !!publishedSnapshot.htmlPart?.[lc]?.trim(),
      hasContent: !!publishedSnapshot.htmlPart?.[lc]?.trim(),
    }));

    this.dialog.open(EmailTemplatePreviewComponent, {
      width: '100%',
      maxWidth: '900px',
      height: '100%',
      maxHeight: '90vh',
      panelClass: 'email-preview-dialog-panel',
      data: {
        templateName: this.emailInitial.templateName,
        htmlPart: publishedSnapshot.htmlPart,
        subjectPart: publishedSnapshot.subjectPart,
        templateVariables: publishedSnapshot.templateVariables,
        defaultLangCode: previewLangCode,
        languageCodes: allLanguageCodes,
        languages,
        defaultEmail: this.storageService.getUser()?.email || '',
        isPublishedVersion: true,
        publishedAt: publishedSnapshot.publishedAt,
      },
    });
  }

  /**
   * View draft version preview
   * Shows the current draft for the specified or currently selected language
   */
  viewDraftVersion(langCode?: string): void {
    const previewLangCode = langCode || this.currLangCode();

    // Build ALL language options (sorted: English first, then alphabetical)
    const allLanguageCodes = sortLanguageCodes(this.languageCodes());
    const languages = allLanguageCodes.map((lc) => ({
      languageCode: lc,
      languageName: this.getLanguageName(lc),
      isValid: !!this.emailForm.value.htmlPart?.[lc]?.trim(),
      hasContent: !!this.emailForm.value.htmlPart?.[lc]?.trim(),
    }));

    this.dialog.open(EmailTemplatePreviewComponent, {
      width: '100%',
      maxWidth: '900px',
      height: '100%',
      maxHeight: '90vh',
      panelClass: 'email-preview-dialog-panel',
      data: {
        templateName: this.emailInitial.templateName,
        htmlPart: this.emailForm.value.htmlPart,
        subjectPart: this.emailForm.value.subjectPart,
        templateVariables: this.emailForm.value.templateVariables,
        defaultLangCode: previewLangCode,
        languageCodes: allLanguageCodes,
        languages,
        defaultEmail: this.storageService.getUser()?.email || '',
        isPublishedVersion: false,
      },
    });
  }

  /**
   * Revert to published version
   */
  async revertToPublished(): Promise<void> {
    if (!this.emailInitial.publishedSnapshot) return;

    const confirmed = await this.alertService.confirm(
      'Revert to Published Version',
      'This will discard all unpublished changes and restore the last published version. This cannot be undone.',
      'Revert',
      'Cancel'
    );

    if (!confirmed) return;

    const snapshot = this.emailInitial.publishedSnapshot;

    for (const langCode of this.languageCodes()) {
      this.templateGroup
        .get(langCode)
        ?.setValue(snapshot.htmlPart[langCode] || '');
      this.subjectGroup
        .get(langCode)
        ?.setValue(snapshot.subjectPart[langCode] || '');
    }
    this.emailForm
      .get('templateVariables')
      ?.setValue([...snapshot.templateVariables]);
    this.emailForm.get('description')?.setValue(snapshot.description);

    this.currentContentHash.set(snapshot.contentHash);
    await this.validateAllLanguages();
    await this.save();

    this.snackBar.open('Reverted to published version', 'Close', {
      duration: 3000,
    });
  }
}
